import React, { useState, useEffect } from 'react';
import { 
  Download, TrendingUp, TrendingDown, DollarSign, Users, Activity, 
  PieChart, Calculator, Clock, Briefcase, BarChart2, Layers, Percent,
  Menu, X, ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

// --- Shared UI Components (Premium Theme - High Contrast) ---

const Card = ({ title, value, subtext, icon: Icon, colorClass }) => (
  <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 hover:border-[#C9A64A]/50 transition-all duration-300 group shadow-xl">
    <div className="flex items-start space-x-4">
      <div className={`p-4 rounded-xl bg-gradient-to-br ${colorClass} shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1">
        <p className="text-xs text-gray-300 font-semibold uppercase tracking-widest mb-2">{title}</p>
        <h3 className="text-3xl font-bold text-white">{value}</h3>
        {subtext && <p className="text-sm text-gray-400 mt-2">{subtext}</p>}
      </div>
    </div>
  </div>
);

const SectionHeader = ({ title, description }) => (
  <div className="mb-8">
    <h2 className="text-4xl md:text-5xl font-bold text-white mb-3 tracking-tight">{title}</h2>
    <p className="text-gray-300 text-lg font-light">{description}</p>
  </div>
);

const InputGroup = ({ label, value, onChange, type = "number", prefix, suffix, min, max, step }) => (
  <div className="mb-5">
    <label className="block text-xs font-bold uppercase text-[#C9A64A] mb-2 tracking-widest">{label}</label>
    <div className="relative">
      {prefix && <span className="absolute left-4 top-3.5 text-gray-400 text-sm font-mono">{prefix}</span>}
      <input 
        type={type}
        value={value} 
        onChange={(e) => onChange(Number(e.target.value))}
        min={min} max={max} step={step}
        className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#C9A64A] focus:border-[#C9A64A] outline-none font-mono text-white placeholder-gray-500 transition-all duration-300 ${prefix ? 'pl-8' : ''} ${suffix ? 'pr-12' : ''}`}
      />
      {suffix && <span className="absolute right-4 top-3.5 text-gray-400 text-sm font-mono">{suffix}</span>}
    </div>
  </div>
);

const fmtCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
const fmtPercent = (num) => `${num.toFixed(1)}%`;
const fmtNum = (num) => new Intl.NumberFormat('en-US').format(num);

// --- Reusable Calculator Wrapper with Tabs ---
const CalculatorWithTabs = ({ title, description, children, howToUse, keyTerms, tips, faqs }) => {
  const [activeSection, setActiveSection] = useState('calculator');

  const sections = [
    { id: 'calculator', name: 'Calculator' },
    { id: 'howto', name: 'How to Use' },
    { id: 'terms', name: 'Key Terms' },
    ...(tips ? [{ id: 'tips', name: 'Pro Tips' }] : []),
    { id: 'faq', name: 'FAQs' },
  ];

  return (
    <div>
      <SectionHeader title={title} description={description} />

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 mb-8 p-1 bg-white/5 rounded-xl">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
              activeSection === section.id 
                ? 'bg-[#C9A64A] text-[#0F1A2A]' 
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            {section.name}
          </button>
        ))}
      </div>

      {activeSection === 'calculator' && children}

      {activeSection === 'howto' && (
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10">
          <h3 className="text-2xl font-bold text-white mb-6">How to Use This Calculator</h3>
          <div className="space-y-6">
            {howToUse.map((step, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C9A64A] flex items-center justify-center text-[#0F1A2A] font-bold">{idx + 1}</div>
                <div>
                  <h4 className="text-lg font-bold text-white mb-2">{step.title}</h4>
                  <p className="text-gray-400">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === 'terms' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {keyTerms.map((term, idx) => (
            <div key={idx} className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <div className={`w-12 h-12 rounded-xl ${term.colorClass || 'bg-[#C9A64A]/20'} flex items-center justify-center mb-4`}>
                {term.icon}
              </div>
              <h4 className="text-xl font-bold text-white mb-3">{term.name}</h4>
              <p className="text-gray-400 leading-relaxed">{term.definition}</p>
            </div>
          ))}
        </div>
      )}

      {activeSection === 'tips' && tips && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tips.map((tip, idx) => (
            <div key={idx} className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <h4 className="text-lg font-bold text-[#C9A64A] mb-3">{tip.title}</h4>
              <p className="text-gray-400 text-sm leading-relaxed">{tip.description}</p>
            </div>
          ))}
        </div>
      )}

      {activeSection === 'faq' && (
        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div key={idx} className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <h4 className="text-lg font-bold text-white mb-3">{faq.q}</h4>
              <p className="text-gray-400 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- 1. SaaS Projection Model (Original) ---
const SaaSModel = () => {
  const [initialCustomers, setInitialCustomers] = useState(100);
  const [arpu, setArpu] = useState(50);
  const [growthRate, setGrowthRate] = useState(10);
  const [churnRate, setChurnRate] = useState(5);
  const [fixedCosts, setFixedCosts] = useState(2000);
  const [variableCostPerUser, setVariableCostPerUser] = useState(5);
  const [projection, setProjection] = useState([]);

  useEffect(() => {
    let data = [];
    let currentCustomers = initialCustomers;
    for (let month = 1; month <= 12; month++) {
      const churnedCustomers = Math.floor(currentCustomers * (churnRate / 100));
      const newCustomers = Math.floor(currentCustomers * (growthRate / 100));
      currentCustomers = currentCustomers - churnedCustomers + newCustomers;
      const revenue = currentCustomers * arpu;
      const variableCosts = currentCustomers * variableCostPerUser;
      const totalExpenses = fixedCosts + variableCosts;
      const profit = revenue - totalExpenses;
      data.push({ month, customers: currentCustomers, newCustomers, churnedCustomers, revenue, expenses: totalExpenses, profit });
    }
    setProjection(data);
  }, [initialCustomers, arpu, growthRate, churnRate, fixedCosts, variableCostPerUser]);

  const exportToCSV = () => {
    let csvContent = "Month,Total Customers,New Customers,Churned Customers,Revenue,Expenses,Net Profit\n";
    projection.forEach(row => {
      csvContent += `${row.month},${row.customers},${row.newCustomers},${row.churnedCustomers},${row.revenue},${row.expenses},${row.profit}\n`;
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "saas_projection.csv";
    link.click();
  };

  const totalRevenue = projection.reduce((acc, curr) => acc + curr.revenue, 0);
  const totalProfit = projection.reduce((acc, curr) => acc + curr.profit, 0);
  const endCustomers = projection.length > 0 ? projection[11].customers : 0;

  const howToUse = [
    { title: 'Enter Starting Customers', description: 'Input your current customer count. This is your baseline for projections.' },
    { title: 'Set Your ARPU', description: 'Average Revenue Per User - the monthly revenue you generate from each customer.' },
    { title: 'Adjust Growth & Churn Rates', description: 'Use the sliders to set your monthly customer growth rate and churn rate.' },
    { title: 'Input Your Costs', description: 'Enter fixed costs (rent, salaries) and variable cost per user (hosting, support).' },
    { title: 'Review 12-Month Projection', description: 'See month-by-month breakdown of customers, revenue, expenses, and profit.' },
  ];

  const keyTerms = [
    { name: 'ARPU', definition: 'Average Revenue Per User - total revenue divided by number of users. Critical metric for SaaS pricing strategy.', icon: <DollarSign className="w-6 h-6 text-[#C9A64A]" />, colorClass: 'bg-[#C9A64A]/20' },
    { name: 'Churn Rate', definition: 'Percentage of customers who cancel their subscription each month. Lower churn = healthier business.', icon: <TrendingDown className="w-6 h-6 text-red-400" />, colorClass: 'bg-red-500/20' },
    { name: 'MRR', definition: 'Monthly Recurring Revenue - predictable revenue generated each month from subscriptions.', icon: <Activity className="w-6 h-6 text-emerald-400" />, colorClass: 'bg-emerald-500/20' },
    { name: 'Net Growth', definition: 'New customers minus churned customers. Positive net growth means your customer base is expanding.', icon: <TrendingUp className="w-6 h-6 text-blue-400" />, colorClass: 'bg-blue-500/20' },
    { name: 'Gross Margin', definition: 'Revenue minus variable costs, divided by revenue. Shows profitability before fixed costs.', icon: <Percent className="w-6 h-6 text-purple-400" />, colorClass: 'bg-purple-500/20' },
    { name: 'ARR', definition: 'Annual Recurring Revenue - MRR × 12. Used for valuation and growth benchmarking.', icon: <BarChart2 className="w-6 h-6 text-orange-400" />, colorClass: 'bg-orange-500/20' },
  ];

  const faqs = [
    { q: 'What is a good monthly growth rate for SaaS?', a: 'For early-stage SaaS, 10-20% monthly growth is considered strong. YC-backed companies often target 7% weekly growth (about 30% monthly). Mature SaaS companies typically see 2-5% monthly growth.' },
    { q: 'What is an acceptable churn rate?', a: 'For B2B SaaS, monthly churn under 2% is good, under 1% is excellent. For B2C SaaS, 5-7% monthly churn is acceptable. Annual churn should ideally be under 10% for B2B.' },
    { q: 'How do I improve my ARPU?', a: 'Strategies include: tiered pricing, usage-based pricing, annual plans with discounts, add-on features, and targeting larger customers (moving upmarket).' },
    { q: 'What costs should I include?', a: 'Fixed costs: salaries, rent, software subscriptions, insurance. Variable costs per user: hosting/infrastructure, customer support, payment processing fees.' },
  ];

  return (
    <CalculatorWithTabs
      title="SaaS Projection Model"
      description="Forecast 12-month growth, revenue, and burn."
      howToUse={howToUse}
      keyTerms={keyTerms}
      faqs={faqs}
    >
      <div>
        <div className="flex justify-end mb-4">
          <button 
            onClick={exportToCSV} 
            className="flex items-center space-x-2 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] hover:scale-105 text-[#0F1A2A] px-5 py-3 rounded-xl text-sm font-bold transition-all duration-300 shadow-lg shadow-[#C9A64A]/20"
          >
            <Download size={16} /> <span>Export CSV</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card title="Run Rate (ARR)" value={fmtCurrency(projection.length > 0 ? projection[11].revenue * 12 : 0)} icon={Activity} colorClass="from-[#C9A64A] to-[#D4B76A]" />
          <Card title="Total Profit" value={fmtCurrency(totalProfit)} icon={DollarSign} colorClass="from-emerald-400 to-emerald-600" />
          <Card title="End Customers" value={fmtNum(endCustomers)} icon={Users} colorClass="from-purple-400 to-purple-600" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1 bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 shadow-xl">
            <h3 className="font-bold mb-6 text-lg text-white">Assumptions</h3>
            <InputGroup label="Starting Customers" value={initialCustomers} onChange={setInitialCustomers} />
            <InputGroup label="ARPU ($)" value={arpu} onChange={setArpu} prefix="$" />
            <InputGroup label="Monthly Growth (%)" value={growthRate} onChange={setGrowthRate} suffix="%" type="range" min="0" max="50" />
            <div className="text-right text-sm font-bold text-[#C9A64A] mb-4">{growthRate}%</div>
            <InputGroup label="Monthly Churn (%)" value={churnRate} onChange={setChurnRate} suffix="%" type="range" min="0" max="20" />
            <div className="text-right text-sm font-bold text-red-400 mb-4">{churnRate}%</div>
            <InputGroup label="Fixed Costs ($)" value={fixedCosts} onChange={setFixedCosts} prefix="$" />
            <InputGroup label="Cost Per User ($)" value={variableCostPerUser} onChange={setVariableCostPerUser} prefix="$" />
          </div>
          <div className="lg:col-span-3 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0F1A2A]/80 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-4 text-left text-[#C9A64A] font-bold uppercase text-xs tracking-wider">Month</th>
                    <th className="px-4 py-4 text-left text-white font-bold uppercase text-xs">Users</th>
                    <th className="px-4 py-4 text-left text-emerald-400 font-bold uppercase text-xs">New</th>
                    <th className="px-4 py-4 text-left text-red-400 font-bold uppercase text-xs">Churn</th>
                    <th className="px-4 py-4 text-left text-white font-bold uppercase text-xs">Rev</th>
                    <th className="px-4 py-4 text-left text-white font-bold uppercase text-xs">Exp</th>
                    <th className="px-4 py-4 text-left text-white font-bold uppercase text-xs">Profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {projection.map((row) => (
                    <tr key={row.month} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 font-bold text-[#C9A64A]">M{row.month}</td>
                      <td className="px-4 py-3 text-white font-mono">{fmtNum(row.customers)}</td>
                      <td className="px-4 py-3 text-emerald-400 font-mono">+{row.newCustomers}</td>
                      <td className="px-4 py-3 text-red-400 font-mono">-{row.churnedCustomers}</td>
                      <td className="px-4 py-3 text-white font-mono">{fmtCurrency(row.revenue)}</td>
                      <td className="px-4 py-3 text-gray-300 font-mono">{fmtCurrency(row.expenses)}</td>
                      <td className={`px-4 py-3 font-bold font-mono ${row.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmtCurrency(row.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 2. CAC Payback Calculator ---
const CACPayback = () => {
  const [salesMarketing, setSalesMarketing] = useState(50000);
  const [newCustomers, setNewCustomers] = useState(100);
  const [arpu, setArpu] = useState(100);
  const [grossMargin, setGrossMargin] = useState(80);

  const cac = salesMarketing / (newCustomers || 1);
  const monthlyGrossProfit = arpu * (grossMargin / 100);
  const paybackMonths = cac / (monthlyGrossProfit || 1);

  const howToUse = [
    { title: 'Enter Sales & Marketing Spend', description: 'Total amount spent on acquiring customers in a given period (monthly or quarterly).' },
    { title: 'Input New Customers Acquired', description: 'Number of new paying customers gained during the same period.' },
    { title: 'Set Your ARPU', description: 'Average Revenue Per User - what each customer pays you monthly.' },
    { title: 'Adjust Gross Margin', description: 'Revenue minus cost of goods sold, expressed as a percentage.' },
  ];

  const keyTerms = [
    { name: 'CAC', definition: 'Customer Acquisition Cost - total sales and marketing spend divided by new customers acquired.', icon: <Users className="w-6 h-6 text-orange-400" />, colorClass: 'bg-orange-500/20' },
    { name: 'Payback Period', definition: 'Months required to recover CAC from a customer\'s gross profit. Under 12 months is typically healthy.', icon: <Clock className="w-6 h-6 text-blue-400" />, colorClass: 'bg-blue-500/20' },
    { name: 'LTV:CAC Ratio', definition: 'Lifetime value divided by CAC. A ratio of 3:1 or higher indicates efficient growth.', icon: <TrendingUp className="w-6 h-6 text-emerald-400" />, colorClass: 'bg-emerald-500/20' },
    { name: 'Gross Margin', definition: 'Percentage of revenue retained after direct costs. Higher margins mean faster payback.', icon: <Percent className="w-6 h-6 text-purple-400" />, colorClass: 'bg-purple-500/20' },
  ];

  const faqs = [
    { q: 'What is a good CAC payback period?', a: 'For SaaS companies, under 12 months is good, under 6 months is excellent. Venture-backed startups can tolerate longer payback (18-24 months) if growth is strong.' },
    { q: 'How can I reduce my CAC?', a: 'Improve conversion rates, leverage organic channels (SEO, content), implement referral programs, optimize ad targeting, and focus on higher-intent leads.' },
    { q: 'Should I include all marketing costs?', a: 'Yes, include salaries, tools, ad spend, events, and any costs directly related to customer acquisition. Some companies also allocate a portion of overhead.' },
  ];

  return (
    <CalculatorWithTabs title="CAC Payback Period" description="Months required to recover customer acquisition cost." howToUse={howToUse} keyTerms={keyTerms} faqs={faqs}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
          <InputGroup label="Sales & Marketing Spend ($)" value={salesMarketing} onChange={setSalesMarketing} prefix="$" />
          <InputGroup label="New Customers Acquired" value={newCustomers} onChange={setNewCustomers} />
          <InputGroup label="ARPU (Monthly)" value={arpu} onChange={setArpu} prefix="$" />
          <InputGroup label="Gross Margin (%)" value={grossMargin} onChange={setGrossMargin} suffix="%" />
        </div>
        <div className="space-y-6">
          <Card title="CAC" value={fmtCurrency(cac)} subtext="Cost to acquire one customer" icon={Users} colorClass="from-orange-400 to-orange-600" />
          <Card title="Monthly Gross Profit" value={fmtCurrency(monthlyGrossProfit)} subtext="Per customer" icon={DollarSign} colorClass="from-blue-400 to-blue-600" />
          <Card title="Payback Period" value={`${paybackMonths.toFixed(1)} Months`} subtext="Time to break even on CAC" icon={Clock} colorClass={paybackMonths <= 12 ? "from-emerald-400 to-emerald-600" : "from-red-400 to-red-600"} />
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 3. Burn Rate & Runway ---
const BurnRunway = () => {
  const [cashBalance, setCashBalance] = useState(500000);
  const [monthlyRevenue, setMonthlyRevenue] = useState(50000);
  const [monthlyExpenses, setMonthlyExpenses] = useState(85000);
  
  const netBurn = monthlyExpenses - monthlyRevenue;
  const runway = netBurn > 0 ? cashBalance / netBurn : Infinity;

  const howToUse = [
    { title: 'Enter Cash Balance', description: 'Your current bank balance - all available cash and equivalents.' },
    { title: 'Input Monthly Revenue', description: 'Average monthly revenue you\'re currently generating.' },
    { title: 'Set Monthly Expenses', description: 'Total monthly operating costs including salaries, rent, tools, etc.' },
  ];

  const keyTerms = [
    { name: 'Gross Burn', definition: 'Total monthly expenses before revenue. Shows your cost structure.', icon: <TrendingDown className="w-6 h-6 text-red-400" />, colorClass: 'bg-red-500/20' },
    { name: 'Net Burn', definition: 'Expenses minus revenue. The actual cash consumed each month.', icon: <DollarSign className="w-6 h-6 text-orange-400" />, colorClass: 'bg-orange-500/20' },
    { name: 'Runway', definition: 'Months of operation remaining at current burn rate. Critical for fundraising timing.', icon: <Activity className="w-6 h-6 text-[#C9A64A]" />, colorClass: 'bg-[#C9A64A]/20' },
    { name: 'Default Alive', definition: 'Paul Graham\'s concept: will you reach profitability before running out of cash?', icon: <TrendingUp className="w-6 h-6 text-emerald-400" />, colorClass: 'bg-emerald-500/20' },
  ];

  const faqs = [
    { q: 'How much runway should I have?', a: 'At minimum 6 months, ideally 12-18 months. Start fundraising when you have 9-12 months left.' },
    { q: 'What is a healthy burn rate?', a: 'Depends on stage. Seed stage: burn should be justified by growth. Series A+: burn should correlate with growth efficiency (burn multiple under 2x).' },
    { q: 'How can I extend runway?', a: 'Reduce expenses, increase revenue, raise capital, negotiate better payment terms with vendors, or consider revenue-based financing.' },
  ];

  return (
    <CalculatorWithTabs title="Burn Rate & Runway" description="Calculate how long until you run out of cash." howToUse={howToUse} keyTerms={keyTerms} faqs={faqs}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
          <InputGroup label="Current Cash Balance" value={cashBalance} onChange={setCashBalance} prefix="$" />
          <InputGroup label="Monthly Revenue" value={monthlyRevenue} onChange={setMonthlyRevenue} prefix="$" />
          <InputGroup label="Monthly Expenses" value={monthlyExpenses} onChange={setMonthlyExpenses} prefix="$" />
        </div>
        <div className="space-y-6">
          <Card title="Net Burn Rate" value={fmtCurrency(netBurn)} subtext="Cash consumed per month" icon={TrendingDown} colorClass="from-red-400 to-red-600" />
          <Card 
            title="Runway" 
            value={runway === Infinity ? "Infinite" : `${runway.toFixed(1)} Months`} 
            subtext="Time until cash zero" 
            icon={Activity} 
            colorClass={runway < 6 ? "from-red-400 to-red-600" : runway < 12 ? "from-yellow-400 to-yellow-600" : "from-emerald-400 to-emerald-600"} 
          />
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 4. LTV Calculator ---
const LTVCalculator = () => {
  const [arpu, setArpu] = useState(100);
  const [churnRate, setChurnRate] = useState(5);
  const [grossMargin, setGrossMargin] = useState(80);

  const customerLifetime = 1 / (churnRate / 100 || 1);
  const ltv = arpu * (grossMargin / 100) * customerLifetime;

  const howToUse = [
    { title: 'Enter Your ARPU', description: 'Average Revenue Per User - monthly revenue from each customer.' },
    { title: 'Set Churn Rate', description: 'Monthly percentage of customers who cancel their subscription.' },
    { title: 'Input Gross Margin', description: 'Revenue minus direct costs (hosting, support), as a percentage.' },
  ];

  const keyTerms = [
    { name: 'LTV', definition: 'Lifetime Value - total gross profit expected from a customer over their entire relationship.', icon: <DollarSign className="w-6 h-6 text-purple-400" />, colorClass: 'bg-purple-500/20' },
    { name: 'Customer Lifetime', definition: 'Average duration a customer stays subscribed. Calculated as 1 / churn rate.', icon: <Clock className="w-6 h-6 text-blue-400" />, colorClass: 'bg-blue-500/20' },
    { name: 'LTV:CAC Ratio', definition: 'Should be at least 3:1 for healthy unit economics. 5:1+ indicates room to invest more in growth.', icon: <TrendingUp className="w-6 h-6 text-emerald-400" />, colorClass: 'bg-emerald-500/20' },
  ];

  const faqs = [
    { q: 'What is a good LTV?', a: 'LTV should be at least 3x your CAC. For enterprise SaaS, LTV can be $50,000+. For SMB SaaS, $1,000-$10,000 is common.' },
    { q: 'How can I increase LTV?', a: 'Reduce churn, increase ARPU through upsells/cross-sells, improve product value, and enhance customer success programs.' },
    { q: 'Should I use monthly or annual churn?', a: 'Use monthly for precision. Annual churn compounds differently: 5% monthly ≈ 46% annual churn, not 60%.' },
  ];

  return (
    <CalculatorWithTabs title="LTV (Lifetime Value)" description="Estimate total gross profit from a single customer." howToUse={howToUse} keyTerms={keyTerms} faqs={faqs}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
          <InputGroup label="ARPU ($)" value={arpu} onChange={setArpu} prefix="$" />
          <InputGroup label="Monthly Churn Rate (%)" value={churnRate} onChange={setChurnRate} suffix="%" />
          <InputGroup label="Gross Margin (%)" value={grossMargin} onChange={setGrossMargin} suffix="%" />
        </div>
        <div className="space-y-6">
          <Card title="Avg Customer Lifetime" value={`${customerLifetime.toFixed(1)} Months`} icon={Clock} colorClass="from-blue-400 to-blue-600" />
          <Card title="LTV" value={fmtCurrency(ltv)} subtext="Lifetime Gross Profit" icon={DollarSign} colorClass="from-purple-400 to-purple-600" />
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 5. Rule of 40 ---
const RuleOf40 = () => {
  const [revenueGrowth, setRevenueGrowth] = useState(30);
  const [profitMargin, setProfitMargin] = useState(15);
  
  const score = revenueGrowth + profitMargin;
  const isHealthy = score >= 40;

  const howToUse = [
    { title: 'Enter Revenue Growth', description: 'Your year-over-year revenue growth rate as a percentage.' },
    { title: 'Input EBITDA Margin', description: 'EBITDA (Earnings Before Interest, Taxes, Depreciation, Amortization) as a percentage of revenue.' },
    { title: 'Check Your Score', description: 'Add growth + margin. 40+ indicates a healthy balance between growth and profitability.' },
  ];

  const keyTerms = [
    { name: 'Rule of 40', definition: 'A SaaS benchmark stating growth rate + profit margin should exceed 40%. Shows if a company balances growth and profitability.', icon: <Percent className="w-6 h-6 text-[#C9A64A]" />, colorClass: 'bg-[#C9A64A]/20' },
    { name: 'EBITDA Margin', definition: 'Operating profitability metric that excludes financing and accounting decisions. EBITDA / Revenue.', icon: <DollarSign className="w-6 h-6 text-emerald-400" />, colorClass: 'bg-emerald-500/20' },
    { name: 'Growth vs Profit Tradeoff', definition: 'High-growth companies often sacrifice profitability. Rule of 40 ensures neither is neglected.', icon: <Activity className="w-6 h-6 text-purple-400" />, colorClass: 'bg-purple-500/20' },
  ];

  const faqs = [
    { q: 'Why is 40 the magic number?', a: 'Empirically, SaaS companies that achieve 40+ tend to have better valuations and outcomes. It represents a healthy balance between growth investment and financial sustainability.' },
    { q: 'What if my score is below 40?', a: 'Not a dealbreaker, especially for early-stage companies. Focus on either accelerating growth or improving unit economics. Many successful companies operate below 40 during hypergrowth phases.' },
    { q: 'Can I score above 60?', a: 'Yes! Scores above 60 are exceptional and often command premium valuations. Companies like Zoom and Datadog have achieved 60+ scores.' },
  ];

  return (
    <CalculatorWithTabs title="Rule of 40" description="SaaS health metric. Growth % + Profit % should be > 40." howToUse={howToUse} keyTerms={keyTerms} faqs={faqs}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
          <InputGroup label="Annual Revenue Growth (%)" value={revenueGrowth} onChange={setRevenueGrowth} suffix="%" />
          <InputGroup label="EBITDA Margin (%)" value={profitMargin} onChange={setProfitMargin} suffix="%" />
        </div>
        <div className="space-y-6">
          <Card 
            title="Rule of 40 Score" 
            value={score.toFixed(1)} 
            subtext={isHealthy ? "Healthy SaaS Company" : "Needs Improvement"} 
            icon={isHealthy ? TrendingUp : TrendingDown} 
            colorClass={isHealthy ? "from-emerald-400 to-emerald-600" : "from-red-400 to-red-600"} 
          />
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 6. Headcount Planner ---
const HeadcountPlanner = () => {
  const [employees, setEmployees] = useState(10);
  const [avgSalary, setAvgSalary] = useState(80000);
  const [benefitsLoad, setBenefitsLoad] = useState(20);
  const [taxRate, setTaxRate] = useState(8);

  const baseTotal = employees * avgSalary;
  const benefitsCost = baseTotal * (benefitsLoad / 100);
  const taxCost = baseTotal * (taxRate / 100);
  const totalCost = baseTotal + benefitsCost + taxCost;

  const howToUse = [
    { title: 'Enter Employee Count', description: 'Total number of employees you want to calculate costs for.' },
    { title: 'Set Average Salary', description: 'Average annual base salary across your team.' },
    { title: 'Adjust Benefits Load', description: 'Health insurance, 401k, etc. as a percentage of salary (typically 15-30%).' },
    { title: 'Input Payroll Tax Rate', description: 'Employer-side taxes (FICA, unemployment). Usually 7-10%.' },
  ];

  const keyTerms = [
    { name: 'Fully Loaded Cost', definition: 'Total cost including salary, benefits, taxes, and sometimes equipment/overhead.', icon: <DollarSign className="w-6 h-6 text-[#C9A64A]" />, colorClass: 'bg-[#C9A64A]/20' },
    { name: 'Benefits Load', definition: 'Additional costs on top of salary: health insurance, retirement, PTO, etc. Typically 20-40% of salary.', icon: <Users className="w-6 h-6 text-blue-400" />, colorClass: 'bg-blue-500/20' },
    { name: 'Payroll Taxes', definition: 'Employer-paid taxes including Social Security (6.2%), Medicare (1.45%), and unemployment.', icon: <Percent className="w-6 h-6 text-red-400" />, colorClass: 'bg-red-500/20' },
  ];

  const faqs = [
    { q: 'What is a typical benefits load?', a: 'For US companies: 20-30% for basic benefits, 30-40% for comprehensive packages including good health insurance and 401k matching.' },
    { q: 'Should I include contractors?', a: 'Contractors don\'t have benefits/tax load but may cost more per hour. Calculate separately and compare total costs.' },
    { q: 'What other costs should I consider?', a: 'Equipment ($2-5k/employee), software licenses, office space ($500-1000/month in cities), recruiting costs (15-25% of salary).' },
  ];

  return (
    <CalculatorWithTabs title="Headcount Cost Planner" description="Estimate fully loaded workforce costs." howToUse={howToUse} keyTerms={keyTerms} faqs={faqs}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
          <InputGroup label="Number of Employees" value={employees} onChange={setEmployees} />
          <InputGroup label="Avg Annual Salary ($)" value={avgSalary} onChange={setAvgSalary} prefix="$" />
          <InputGroup label="Benefits Load (%)" value={benefitsLoad} onChange={setBenefitsLoad} suffix="%" />
          <InputGroup label="Payroll Tax (%)" value={taxRate} onChange={setTaxRate} suffix="%" />
        </div>
        <div className="space-y-6">
          <Card title="Total Payroll Cost" value={fmtCurrency(totalCost)} subtext="Annual Fully Loaded" icon={Users} colorClass="from-indigo-400 to-indigo-600" />
          <Card title="Cost Per Employee" value={fmtCurrency(totalCost / (employees || 1))} subtext="Effective Annual Cost" icon={Briefcase} colorClass="from-slate-400 to-slate-600" />
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 7. Break-even Analysis ---
const BreakEven = () => {
  const [fixedCosts, setFixedCosts] = useState(10000);
  const [pricePerUnit, setPricePerUnit] = useState(50);
  const [costPerUnit, setCostPerUnit] = useState(20);

  const contributionMargin = pricePerUnit - costPerUnit;
  const breakEvenUnits = contributionMargin > 0 ? fixedCosts / contributionMargin : 0;
  const breakEvenRevenue = breakEvenUnits * pricePerUnit;

  const howToUse = [
    { title: 'Enter Fixed Costs', description: 'Total fixed costs that don\'t change with sales volume (rent, salaries, software).' },
    { title: 'Set Price Per Unit', description: 'How much you charge for each unit or subscription.' },
    { title: 'Input Variable Cost', description: 'Cost to produce/deliver each unit (COGS, support per customer).' },
  ];

  const keyTerms = [
    { name: 'Break-even Point', definition: 'Sales volume where total revenue equals total costs. No profit, no loss.', icon: <Layers className="w-6 h-6 text-blue-400" />, colorClass: 'bg-blue-500/20' },
    { name: 'Contribution Margin', definition: 'Price minus variable cost per unit. Amount each sale contributes to covering fixed costs.', icon: <DollarSign className="w-6 h-6 text-emerald-400" />, colorClass: 'bg-emerald-500/20' },
    { name: 'Margin of Safety', definition: 'How far above break-even you\'re operating. Higher margin = more buffer against downturns.', icon: <TrendingUp className="w-6 h-6 text-[#C9A64A]" />, colorClass: 'bg-[#C9A64A]/20' },
  ];

  const faqs = [
    { q: 'How do I use break-even for pricing?', a: 'Calculate break-even at different price points. Higher prices = fewer sales needed, but may reduce demand. Find the optimal balance.' },
    { q: 'What about SaaS companies?', a: 'For SaaS, fixed costs are high (development, infrastructure) but variable costs per user are low, leading to high contribution margins after scale.' },
    { q: 'Should I include all fixed costs?', a: 'Yes, include rent, salaries, software, insurance - any cost that doesn\'t change based on sales volume.' },
  ];

  return (
    <CalculatorWithTabs title="Break-even Analysis" description="Determine sales volume needed to cover costs." howToUse={howToUse} keyTerms={keyTerms} faqs={faqs}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
          <InputGroup label="Total Fixed Costs ($)" value={fixedCosts} onChange={setFixedCosts} prefix="$" />
          <InputGroup label="Price Per Unit ($)" value={pricePerUnit} onChange={setPricePerUnit} prefix="$" />
          <InputGroup label="Variable Cost Per Unit ($)" value={costPerUnit} onChange={setCostPerUnit} prefix="$" />
        </div>
        <div className="space-y-6">
          <Card title="Break-even Units" value={fmtNum(Math.ceil(breakEvenUnits))} subtext="Units to sell" icon={Layers} colorClass="from-blue-400 to-blue-600" />
          <Card title="Break-even Revenue" value={fmtCurrency(breakEvenRevenue)} subtext="Revenue needed" icon={DollarSign} colorClass="from-teal-400 to-teal-600" />
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 8. CAGR Calculator ---
const CAGRCalc = () => {
  const [startValue, setStartValue] = useState(100000);
  const [endValue, setEndValue] = useState(250000);
  const [years, setYears] = useState(3);

  const cagr = years > 0 ? (Math.pow(endValue / startValue, 1 / years) - 1) * 100 : 0;

  const howToUse = [
    { title: 'Enter Starting Value', description: 'Initial value at the beginning of the period (revenue, investment, etc.).' },
    { title: 'Input Ending Value', description: 'Final value at the end of the period.' },
    { title: 'Set Time Period', description: 'Number of years between start and end values.' },
  ];

  const keyTerms = [
    { name: 'CAGR', definition: 'Compound Annual Growth Rate - the smoothed annual growth rate that would get you from start to end value.', icon: <TrendingUp className="w-6 h-6 text-emerald-400" />, colorClass: 'bg-emerald-500/20' },
    { name: 'Absolute Growth', definition: 'Simple percentage change from start to end, without annualizing.', icon: <Percent className="w-6 h-6 text-blue-400" />, colorClass: 'bg-blue-500/20' },
    { name: 'Year-over-Year', definition: 'Growth comparing the same period across years. CAGR smooths out YoY fluctuations.', icon: <Activity className="w-6 h-6 text-[#C9A64A]" />, colorClass: 'bg-[#C9A64A]/20' },
  ];

  const faqs = [
    { q: 'Why use CAGR instead of average growth?', a: 'CAGR accounts for compounding, giving a more accurate picture of growth. Simple averages can be misleading when growth varies year to year.' },
    { q: 'What is a good CAGR?', a: 'Depends on context. SaaS: 30-50% is strong. Mature businesses: 5-15%. Stock market average: ~10% historically.' },
    { q: 'Does CAGR account for volatility?', a: 'No, CAGR smooths out all volatility. A company could have wild swings but the same CAGR as a steady grower.' },
  ];

  return (
    <CalculatorWithTabs title="CAGR Calculator" description="Compound Annual Growth Rate." howToUse={howToUse} keyTerms={keyTerms} faqs={faqs}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
          <InputGroup label="Starting Value ($)" value={startValue} onChange={setStartValue} prefix="$" />
          <InputGroup label="Ending Value ($)" value={endValue} onChange={setEndValue} prefix="$" />
          <InputGroup label="Number of Years" value={years} onChange={setYears} />
        </div>
        <div className="space-y-6">
          <Card title="CAGR" value={fmtPercent(cagr)} subtext="Annualized Growth Rate" icon={TrendingUp} colorClass="from-green-400 to-green-600" />
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 9. Working Capital ---
const WorkingCapital = () => {
  const [currentAssets, setCurrentAssets] = useState(150000);
  const [currentLiabilities, setCurrentLiabilities] = useState(80000);

  const workingCapital = currentAssets - currentLiabilities;
  const currentRatio = currentLiabilities > 0 ? currentAssets / currentLiabilities : 0;

  const howToUse = [
    { title: 'Enter Current Assets', description: 'Cash, accounts receivable, inventory, and other assets convertible to cash within a year.' },
    { title: 'Input Current Liabilities', description: 'Accounts payable, short-term debt, and other obligations due within a year.' },
  ];

  const keyTerms = [
    { name: 'Working Capital', definition: 'Current assets minus current liabilities. Shows ability to fund day-to-day operations.', icon: <DollarSign className="w-6 h-6 text-blue-400" />, colorClass: 'bg-blue-500/20' },
    { name: 'Current Ratio', definition: 'Current assets divided by current liabilities. Above 1.5 is typically healthy.', icon: <Activity className="w-6 h-6 text-emerald-400" />, colorClass: 'bg-emerald-500/20' },
    { name: 'Quick Ratio', definition: 'Like current ratio but excludes inventory. More conservative liquidity measure.', icon: <Percent className="w-6 h-6 text-[#C9A64A]" />, colorClass: 'bg-[#C9A64A]/20' },
  ];

  const faqs = [
    { q: 'What is a good current ratio?', a: 'Generally 1.5-2.0 is healthy. Below 1.0 means you may struggle to pay short-term obligations. Too high (>3.0) might mean inefficient use of assets.' },
    { q: 'Can working capital be negative?', a: 'Yes, and it\'s not always bad. Some businesses (like Amazon) operate with negative working capital because they collect from customers before paying suppliers.' },
    { q: 'How can I improve working capital?', a: 'Collect receivables faster, negotiate longer payment terms with suppliers, reduce inventory levels, or secure a line of credit.' },
  ];

  return (
    <CalculatorWithTabs title="Working Capital" description="Assess liquidity and short-term financial health." howToUse={howToUse} keyTerms={keyTerms} faqs={faqs}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
          <InputGroup label="Current Assets ($)" value={currentAssets} onChange={setCurrentAssets} prefix="$" />
          <InputGroup label="Current Liabilities ($)" value={currentLiabilities} onChange={setCurrentLiabilities} prefix="$" />
        </div>
        <div className="space-y-6">
          <Card title="Net Working Capital" value={fmtCurrency(workingCapital)} icon={DollarSign} colorClass="from-blue-400 to-blue-600" />
          <Card 
            title="Current Ratio" 
            value={currentRatio.toFixed(2)} 
            subtext={currentRatio >= 1.5 ? "Healthy (>1.5)" : "Caution (<1.5)"} 
            icon={Activity} 
            colorClass={currentRatio >= 1.5 ? "from-emerald-400 to-emerald-600" : "from-orange-400 to-orange-600"} 
          />
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 10. Discounted Cash Flow (Simple DCF) ---
const DCFModel = () => {
  const [freeCashFlow, setFreeCashFlow] = useState(100000);
  const [growthRate, setGrowthRate] = useState(5);
  const [discountRate, setDiscountRate] = useState(10);
  const [years, setYears] = useState(5);
  const [terminalGrowth, setTerminalGrowth] = useState(2);

  const calculateDCF = () => {
    let totalPV = 0;
    let currentFCF = freeCashFlow;

    for (let i = 1; i <= years; i++) {
      currentFCF = currentFCF * (1 + growthRate / 100);
      const pv = currentFCF / Math.pow(1 + discountRate / 100, i);
      totalPV += pv;
    }

    // Terminal Value
    const terminalValue = (currentFCF * (1 + terminalGrowth / 100)) / (discountRate / 100 - terminalGrowth / 100);
    const terminalPV = terminalValue / Math.pow(1 + discountRate / 100, years);

    return totalPV + terminalPV;
  };

  const howToUse = [
    { title: 'Enter Current Free Cash Flow', description: 'Cash generated after operating expenses and capital expenditures.' },
    { title: 'Set Growth Rate', description: 'Expected annual growth in free cash flow over the projection period.' },
    { title: 'Input Discount Rate', description: 'Usually WACC (Weighted Average Cost of Capital). Reflects risk of the investment.' },
    { title: 'Set Terminal Growth', description: 'Long-term perpetual growth rate (usually 2-3%, close to GDP growth).' },
  ];

  const keyTerms = [
    { name: 'Free Cash Flow', definition: 'Cash available after operating expenses and capital investments. The true measure of financial performance.', icon: <DollarSign className="w-6 h-6 text-emerald-400" />, colorClass: 'bg-emerald-500/20' },
    { name: 'Discount Rate', definition: 'Rate used to convert future cash flows to present value. Higher rate = lower valuation.', icon: <Percent className="w-6 h-6 text-blue-400" />, colorClass: 'bg-blue-500/20' },
    { name: 'Terminal Value', definition: 'Value of all cash flows beyond the projection period. Often 60-80% of total DCF value.', icon: <TrendingUp className="w-6 h-6 text-purple-400" />, colorClass: 'bg-purple-500/20' },
    { name: 'NPV', definition: 'Net Present Value - sum of all discounted future cash flows. The intrinsic value of the business.', icon: <Calculator className="w-6 h-6 text-[#C9A64A]" />, colorClass: 'bg-[#C9A64A]/20' },
  ];

  const faqs = [
    { q: 'What discount rate should I use?', a: 'For public companies, use WACC (typically 8-12%). For startups, use 25-40%+ to reflect higher risk. VC expected returns are 25-35%.' },
    { q: 'Why is terminal value so large?', a: 'Terminal value captures all cash flows in perpetuity. It\'s a mathematical artifact - be conservative with terminal growth rate (2-3% max).' },
    { q: 'How reliable is DCF for startups?', a: 'Less reliable due to uncertain cash flows. DCF works better for mature businesses. For startups, use comparable multiples or VC method instead.' },
  ];

  return (
    <CalculatorWithTabs title="Simple DCF Valuation" description="Estimate the value of an investment based on future cash flows." howToUse={howToUse} keyTerms={keyTerms} faqs={faqs}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
          <InputGroup label="Current Free Cash Flow ($)" value={freeCashFlow} onChange={setFreeCashFlow} prefix="$" />
          <InputGroup label="Projected Annual Growth (%)" value={growthRate} onChange={setGrowthRate} suffix="%" />
          <InputGroup label="Discount Rate (WACC) (%)" value={discountRate} onChange={setDiscountRate} suffix="%" />
          <InputGroup label="Terminal Growth Rate (%)" value={terminalGrowth} onChange={setTerminalGrowth} suffix="%" />
        </div>
        <div className="space-y-6">
          <Card title="Estimated Value (NPV)" value={fmtCurrency(calculateDCF())} subtext="Present Value of Future Flows" icon={DollarSign} colorClass="from-violet-400 to-violet-600" />
        </div>
      </div>
    </CalculatorWithTabs>
  );
};

// --- 11. SaaS Valuation Calculator ---
const SaaSValuationCalculator = () => {
  const [arr, setArr] = useState(1000000);
  const [growthRate, setGrowthRate] = useState(30);
  const [activeSection, setActiveSection] = useState('calculator');

  // David Cumming's SaaS Valuation Formula
  // Valuation = 2 x ARR + ARR x (1 + 2.5 x Growth Rate)
  const calculateValuation = () => {
    const growthDecimal = growthRate / 100;
    return 2 * arr + arr * (1 + 2.5 * growthDecimal);
  };

  // Alternative multiple-based valuation
  const getValuationMultiple = () => {
    if (growthRate >= 100) return 15;
    if (growthRate >= 75) return 12;
    if (growthRate >= 50) return 10;
    if (growthRate >= 30) return 8;
    if (growthRate >= 20) return 6;
    if (growthRate >= 10) return 5;
    return 4;
  };

  const multipleBasedValuation = arr * getValuationMultiple();
  const cummingValuation = calculateValuation();
  const ruleOf40Score = growthRate + 15; // Assuming 15% profit margin for demo

  const sections = [
    { id: 'calculator', name: 'Calculator' },
    { id: 'howto', name: 'How to Use' },
    { id: 'terms', name: 'Key Terms' },
    { id: 'increase', name: 'Increase Valuation' },
    { id: 'faq', name: 'FAQs' },
  ];

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <SectionHeader 
          title="SaaS Valuation Calculator" 
          description="Calculate your SaaS company&apos;s valuation using industry-standard formulas."
        />
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 mb-8 p-1 bg-white/5 rounded-xl">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
              activeSection === section.id 
                ? 'bg-[#C9A64A] text-[#0F1A2A]' 
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            {section.name}
          </button>
        ))}
      </div>

      {activeSection === 'calculator' && (
        <>
          {/* Main Calculator Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Input Panel */}
            <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 shadow-xl">
              <h3 className="font-bold mb-6 text-xl text-white flex items-center gap-2">
                <Calculator className="w-5 h-5 text-[#C9A64A]" />
                Enter Your Metrics
              </h3>
              
              {/* ARR Input */}
              <div className="mb-8">
                <label className="block text-xs font-bold uppercase text-[#C9A64A] mb-3 tracking-widest">
                  Annual Recurring Revenue (ARR)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-4 text-gray-400 text-lg font-mono">$</span>
                  <input 
                    type="number"
                    value={arr} 
                    onChange={(e) => setArr(Number(e.target.value))}
                    className="w-full px-4 py-4 pl-10 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#C9A64A] focus:border-[#C9A64A] outline-none font-mono text-2xl text-white transition-all duration-300"
                  />
                </div>
                <input 
                  type="range"
                  value={arr} 
                  onChange={(e) => setArr(Number(e.target.value))}
                  min="100000" max="50000000" step="100000"
                  className="w-full mt-4 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#C9A64A]"
                />
                <div className="flex justify-between mt-2 text-xs text-gray-400">
                  <span>$100K</span>
                  <span>$50M</span>
                </div>
              </div>

              {/* Growth Rate Input */}
              <div className="mb-6">
                <label className="block text-xs font-bold uppercase text-[#C9A64A] mb-3 tracking-widest">
                  Annual Growth Rate
                </label>
                <div className="text-center mb-4">
                  <span className="text-5xl font-bold text-white">{growthRate}</span>
                  <span className="text-2xl text-[#C9A64A] ml-1">%</span>
                </div>
                <input 
                  type="range"
                  value={growthRate} 
                  onChange={(e) => setGrowthRate(Number(e.target.value))}
                  min="0" max="150" step="5"
                  className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#C9A64A]"
                />
                <div className="flex justify-between mt-2 text-xs text-gray-400">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                  <span>150%</span>
                </div>
              </div>

              {/* Growth Rate Indicator */}
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Growth Classification</span>
                  <span className={`font-bold ${
                    growthRate >= 50 ? 'text-emerald-400' : 
                    growthRate >= 30 ? 'text-[#C9A64A]' : 
                    growthRate >= 15 ? 'text-yellow-400' : 'text-orange-400'
                  }`}>
                    {growthRate >= 50 ? 'Hypergrowth' : 
                     growthRate >= 30 ? 'High Growth' : 
                     growthRate >= 15 ? 'Moderate Growth' : 'Low Growth'}
                  </span>
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="space-y-6">
              {/* Primary Valuation */}
              <div className="bg-gradient-to-br from-[#C9A64A]/20 to-[#D4B76A]/10 backdrop-blur-xl p-8 rounded-2xl border border-[#C9A64A]/30 shadow-xl">
                <p className="text-[#C9A64A] text-xs font-bold tracking-widest uppercase mb-2">Your SaaS Valuation</p>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {fmtCurrency(cummingValuation)}
                </h2>
                <p className="text-gray-400 text-sm">Based on David Cumming&apos;s Formula</p>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs text-gray-400 font-mono">
                    = 2 × ${fmtNum(arr)} + ${fmtNum(arr)} × (1 + 2.5 × {growthRate}%)
                  </p>
                </div>
              </div>

              {/* Secondary Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
                  <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Multiple-Based</p>
                  <p className="text-2xl font-bold text-white">{fmtCurrency(multipleBasedValuation)}</p>
                  <p className="text-[#C9A64A] text-sm font-semibold mt-1">{getValuationMultiple()}x ARR</p>
                </div>
                <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
                  <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Rule of 40</p>
                  <p className={`text-2xl font-bold ${ruleOf40Score >= 40 ? 'text-emerald-400' : 'text-orange-400'}`}>
                    {ruleOf40Score}%
                  </p>
                  <p className="text-gray-400 text-sm mt-1">{ruleOf40Score >= 40 ? 'Healthy' : 'Below Target'}</p>
                </div>
              </div>

              {/* Valuation Range */}
              <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-4">Valuation Range Estimate</p>
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="text-lg font-bold text-white">{fmtCurrency(arr * (getValuationMultiple() - 2))}</p>
                    <p className="text-xs text-gray-400">Conservative</p>
                  </div>
                  <div className="flex-1 mx-4 h-2 bg-gradient-to-r from-orange-500 via-[#C9A64A] to-emerald-500 rounded-full" />
                  <div className="text-center">
                    <p className="text-lg font-bold text-white">{fmtCurrency(arr * (getValuationMultiple() + 2))}</p>
                    <p className="text-xs text-gray-400">Optimistic</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Comparison Table */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h3 className="font-bold text-lg text-white">Valuation Multiple Benchmarks</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0F1A2A]/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-[#C9A64A] font-bold uppercase text-xs">Growth Rate</th>
                    <th className="px-6 py-4 text-left text-white font-bold uppercase text-xs">Typical Multiple</th>
                    <th className="px-6 py-4 text-left text-white font-bold uppercase text-xs">Your ARR Valuation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    { range: '100%+', multiple: '12-15x', highlight: growthRate >= 100 },
                    { range: '50-100%', multiple: '8-12x', highlight: growthRate >= 50 && growthRate < 100 },
                    { range: '30-50%', multiple: '6-8x', highlight: growthRate >= 30 && growthRate < 50 },
                    { range: '15-30%', multiple: '4-6x', highlight: growthRate >= 15 && growthRate < 30 },
                    { range: '<15%', multiple: '2-4x', highlight: growthRate < 15 },
                  ].map((row, idx) => (
                    <tr key={idx} className={`transition-colors ${row.highlight ? 'bg-[#C9A64A]/10' : 'hover:bg-white/5'}`}>
                      <td className={`px-6 py-4 font-semibold ${row.highlight ? 'text-[#C9A64A]' : 'text-gray-300'}`}>{row.range}</td>
                      <td className="px-6 py-4 text-white font-mono">{row.multiple}</td>
                      <td className="px-6 py-4 text-white font-mono">
                        {row.highlight && fmtCurrency(multipleBasedValuation)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeSection === 'howto' && (
        <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10">
          <h3 className="text-2xl font-bold text-white mb-6">How to Use the SaaS Valuation Calculator</h3>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C9A64A] flex items-center justify-center text-[#0F1A2A] font-bold">1</div>
              <div>
                <h4 className="text-lg font-bold text-white mb-2">Enter Your Annual Recurring Revenue (ARR)</h4>
                <p className="text-gray-400">Use the slider or input field to specify your company&apos;s ARR. This represents the predictable yearly revenue generated from subscriptions.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C9A64A] flex items-center justify-center text-[#0F1A2A] font-bold">2</div>
              <div>
                <h4 className="text-lg font-bold text-white mb-2">Specify Your Annual Growth Rate</h4>
                <p className="text-gray-400">Adjust the growth rate slider to reflect how much your revenue is increasing per year. This percentage is critical for determining your valuation multiple.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C9A64A] flex items-center justify-center text-[#0F1A2A] font-bold">3</div>
              <div>
                <h4 className="text-lg font-bold text-white mb-2">View Your Valuation</h4>
                <p className="text-gray-400">The calculator computes an estimated valuation using David Cumming&apos;s formula, giving you an idea of your company&apos;s worth.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C9A64A] flex items-center justify-center text-[#0F1A2A] font-bold">4</div>
              <div>
                <h4 className="text-lg font-bold text-white mb-2">Iterate and Experiment</h4>
                <p className="text-gray-400">Try different ARR or growth projections to see how changes impact your valuation. This helps with scenario planning and fundraising preparation.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-6 bg-[#C9A64A]/10 rounded-xl border border-[#C9A64A]/30">
            <h4 className="text-lg font-bold text-[#C9A64A] mb-2">📊 The Formula We Use</h4>
            <p className="text-gray-300 mb-4">This calculator uses David Cumming&apos;s valuation formula for SaaS companies:</p>
            <code className="block bg-[#0F1A2A] p-4 rounded-lg text-[#C9A64A] font-mono text-lg">
              Valuation = 2 × ARR + ARR × (1 + 2.5 × Growth Rate)
            </code>
            <p className="text-gray-400 text-sm mt-4">This formula assumes typical SaaS gross margins of 70-80% and renewal rates of 80-90%.</p>
          </div>
        </div>
      )}

      {activeSection === 'terms' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
            <div className="w-12 h-12 rounded-xl bg-[#C9A64A]/20 flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-[#C9A64A]" />
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Annual Recurring Revenue (ARR)</h4>
            <p className="text-gray-400 leading-relaxed">
              ARR represents the total predictable revenue your business generates yearly through subscriptions. 
              For example, if you charge $10,000 annually per client and have 50 clients, your ARR would be $500,000.
            </p>
          </div>
          
          <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-emerald-400" />
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Growth Rate</h4>
            <p className="text-gray-400 leading-relaxed">
              Growth rate reflects the annual increase in your company&apos;s revenue, expressed as a percentage. 
              If your ARR grew from $500K to $750K, your growth rate is 50%. Higher growth = higher valuation multiples.
            </p>
          </div>
          
          <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
              <PieChart className="w-6 h-6 text-purple-400" />
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Valuation Multiple</h4>
            <p className="text-gray-400 leading-relaxed">
              A multiplier applied to your ARR to estimate company value. SaaS multiples typically range from 5x to 12x ARR, 
              depending on growth rate, profitability, and market conditions.
            </p>
          </div>
          
          <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
              <Percent className="w-6 h-6 text-blue-400" />
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Rule of 40</h4>
            <p className="text-gray-400 leading-relaxed">
              A benchmark stating that growth rate + profit margin should equal or exceed 40%. 
              For example, 30% growth + 15% margin = 45%, indicating a well-balanced, healthy SaaS business.
            </p>
          </div>
          
          <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-orange-400" />
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Net Revenue Retention (NRR)</h4>
            <p className="text-gray-400 leading-relaxed">
              Measures revenue retained from existing customers including expansions and contractions. 
              NRR above 100% means you&apos;re growing revenue from existing customers without new sales.
            </p>
          </div>
          
          <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center mb-4">
              <TrendingDown className="w-6 h-6 text-red-400" />
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Churn Rate</h4>
            <p className="text-gray-400 leading-relaxed">
              The percentage of customers or revenue lost over a period. Lower churn signals product-market fit 
              and customer satisfaction, directly impacting valuation positively.
            </p>
          </div>
        </div>
      )}

      {activeSection === 'increase' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-emerald-400" />
              Ways to Increase Your SaaS Valuation
            </h3>
            <p className="text-gray-300">Focus on these proven strategies to improve your company&apos;s valuation.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <h4 className="text-lg font-bold text-[#C9A64A] mb-3">💰 Optimize Pricing</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                Adjust pricing to keep pace with inflation and market trends. Experiment with tiered plans or value-based pricing 
                to maximize revenue while maintaining customer flexibility.
              </p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <h4 className="text-lg font-bold text-[#C9A64A] mb-3">📋 Document Processes</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                Document every critical workflow from customer acquisition to retention. These playbooks improve efficiency 
                and are valuable to potential acquirers looking for scalable operations.
              </p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <h4 className="text-lg font-bold text-[#C9A64A] mb-3">⭐ Enhance Customer Experience</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                Exceptional experiences reduce churn and turn customers into evangelists. Focus on seamless onboarding 
                and proactive support to boost your NPS—a key indicator for investors.
              </p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <h4 className="text-lg font-bold text-[#C9A64A] mb-3">🔒 Protect Your IP</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                Trademark your brand, secure patents for technology, and copyright original content. 
                Protected IP signals to investors that your company is a pioneer worth a premium.
              </p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <h4 className="text-lg font-bold text-[#C9A64A] mb-3">📈 Improve Unit Economics</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                Focus on LTV:CAC ratio (aim for 3:1+), reduce payback period, and improve gross margins. 
                Strong unit economics signal sustainable, profitable growth potential.
              </p>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <h4 className="text-lg font-bold text-[#C9A64A] mb-3">🎯 Reduce Churn</h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                Even small reductions in churn compound dramatically over time. Implement customer success programs, 
                improve onboarding, and proactively address at-risk accounts.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeSection === 'faq' && (
        <div className="space-y-4">
          {[
            {
              q: 'What are SaaS valuation multiples?',
              a: 'SaaS valuation multiples are benchmarks reflecting how much a company is worth relative to its ARR. Multiples typically range from 5x to 12x ARR, depending on growth rate, profitability, and market conditions. High-growth businesses with strong retention can secure multiples at the higher end.'
            },
            {
              q: 'What is the Rule of 40 in SaaS valuation?',
              a: 'The Rule of 40 states that a SaaS company\'s growth rate plus its profit margin should equal or exceed 40%. For example, 30% growth + 15% profit margin = 45%. This indicates a well-balanced business that\'s both growing and sustainable.'
            },
            {
              q: 'What is the average valuation multiple for SaaS?',
              a: 'The average multiple typically falls between 10x and 15x ARR, meaning a company with $1M ARR could be valued between $10M and $15M. However, this varies based on market environment and individual metrics. Companies with innovative solutions and high retention often achieve above-average multiples.'
            },
            {
              q: 'How accurate is this calculator?',
              a: 'This calculator provides an estimate based on David Cumming\'s formula and industry benchmarks. Actual valuations depend on many factors including market conditions, competitive landscape, team strength, and investor appetite. Use this as a starting point for discussions with financial advisors.'
            },
            {
              q: 'What factors affect SaaS valuation beyond ARR and growth?',
              a: 'Key factors include: Net Revenue Retention (NRR), gross margins, customer concentration, market size (TAM), competitive moat, team experience, capital efficiency, and path to profitability. Strong metrics across these areas can significantly boost your multiple.'
            },
          ].map((faq, idx) => (
            <div key={idx} className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
              <h4 className="text-lg font-bold text-white mb-3">{faq.q}</h4>
              <p className="text-gray-400 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Component for EOS sliders
const ComponentSlider = ({ label, value, onChange, icon, description, colorClass }) => (
  <div className="mb-6">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <span className={`text-${colorClass}-400`}>{icon}</span>
        <div>
          <label className="text-sm font-semibold text-white">{label}</label>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </div>
      <span className={`text-lg font-bold ${value >= 7 ? 'text-emerald-400' : value >= 4 ? 'text-[#C9A64A]' : 'text-red-400'}`}>
        {value}/10
      </span>
    </div>
    <input 
      type="range"
      value={value} 
      onChange={(e) => onChange(Number(e.target.value))}
      min="1" max="10" step="1"
      className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#C9A64A]"
    />
  </div>
);

// --- 12. EOS® (Entrepreneurial Operating System) Calculator ---
const EOSCalculator = () => {
  const [activeView, setActiveView] = useState('health'); // health, vto, rocks, l10, scorecard, people
  
  // Health Check State
  const [vision, setVision] = useState(5);
  const [people, setPeople] = useState(6);
  const [data, setData] = useState(4);
  const [issues, setIssues] = useState(5);
  const [process, setProcess] = useState(3);
  const [traction, setTraction] = useState(5);

  // V/TO State
  const [vto, setVto] = useState({
    coreValues: ['', '', ''],
    coreFocus: { purpose: '', niche: '' },
    tenYearTarget: '',
    threeYearPicture: { revenue: '', profit: '', measurables: '' },
    oneYearPlan: { revenue: '', profit: '', goals: ['', '', ''] },
    rocks: ['', '', '']
  });

  // Rocks State
  const [rocks, setRocks] = useState([
    { id: 1, owner: '', rock: '', dueDate: '', status: 'on-track' },
    { id: 2, owner: '', rock: '', dueDate: '', status: 'on-track' },
    { id: 3, owner: '', rock: '', dueDate: '', status: 'on-track' },
  ]);

  // L10 Meeting State
  const [l10, setL10] = useState({
    segue: '',
    scorecardNotes: '',
    rockReview: '',
    headlines: '',
    todos: ['', '', ''],
    issuesList: ['', '', ''],
    conclude: ''
  });

  // Scorecard State
  const [scorecard, setScorecard] = useState([
    { id: 1, measurable: 'Revenue', owner: '', goal: '', w1: '', w2: '', w3: '', w4: '' },
    { id: 2, measurable: 'New Leads', owner: '', goal: '', w1: '', w2: '', w3: '', w4: '' },
    { id: 3, measurable: 'Conversion Rate', owner: '', goal: '', w1: '', w2: '', w3: '', w4: '' },
  ]);

  // People Analyzer State
  const [peopleAnalyzer, setPeopleAnalyzer] = useState({
    coreValues: ['Integrity', 'Growth', 'Teamwork'],
    team: [
      { name: '', role: '', v1: 0, v2: 0, v3: 0, getsIt: false, wantsIt: false, capacity: false },
    ]
  });

  // Calculate EOS score
  const eosScore = Math.round((vision + people + data + issues + process + traction) / 6 * 10);
  
  const getTractionLevel = () => {
    if (eosScore >= 85) return { level: 'Running on EOS®', color: 'text-emerald-400' };
    if (eosScore >= 70) return { level: 'Strong Traction', color: 'text-[#C9A64A]' };
    if (eosScore >= 50) return { level: 'Getting Traction', color: 'text-yellow-400' };
    if (eosScore >= 30) return { level: 'Early Stage', color: 'text-orange-400' };
    return { level: 'Pre-EOS', color: 'text-red-400' };
  };

  const tractionLevel = getTractionLevel();

  const tools = [
    { id: 'health', name: 'Health Check', icon: <Activity className="w-4 h-4" /> },
    { id: 'vto', name: 'V/TO™', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'rocks', name: 'Rocks', icon: <Layers className="w-4 h-4" /> },
    { id: 'l10', name: 'L10 Meeting', icon: <Clock className="w-4 h-4" /> },
    { id: 'scorecard', name: 'Scorecard', icon: <BarChart2 className="w-4 h-4" /> },
    { id: 'people', name: 'People Analyzer', icon: <Users className="w-4 h-4" /> },
  ];

  // Health Check View
  const HealthCheckView = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-[#C9A64A]/20 to-[#D4B76A]/10 p-8 rounded-2xl border border-[#C9A64A]/30 text-center">
          <p className="text-[#C9A64A] text-xs font-bold tracking-widest uppercase mb-2">EOS® Score</p>
          <div className="relative inline-flex items-center justify-center">
            <svg className="w-28 h-28 transform -rotate-90">
              <circle cx="56" cy="56" r="48" stroke="currentColor" strokeWidth="8" fill="none" className="text-white/10" />
              <circle cx="56" cy="56" r="48" stroke="currentColor" strokeWidth="8" fill="none" 
                strokeDasharray={`${eosScore * 3.02} 302`} className="text-[#C9A64A]" />
            </svg>
            <span className="absolute text-3xl font-bold text-white">{eosScore}</span>
          </div>
          <p className={`text-lg font-bold ${tractionLevel.color} mt-3`}>{tractionLevel.level}</p>
        </div>
        <div className="lg:col-span-2 bg-white/5 p-6 rounded-2xl border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4">Rate Your 6 Components</h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Vision', value: vision, set: setVision },
              { label: 'People', value: people, set: setPeople },
              { label: 'Data', value: data, set: setData },
              { label: 'Issues', value: issues, set: setIssues },
              { label: 'Process', value: process, set: setProcess },
              { label: 'Traction', value: traction, set: setTraction },
            ].map((comp, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-gray-400 text-sm w-16">{comp.label}</span>
                <input type="range" value={comp.value} onChange={(e) => comp.set(Number(e.target.value))}
                  min="1" max="10" className="flex-1 h-2 bg-white/10 rounded-lg accent-[#C9A64A]" />
                <span className={`font-bold w-6 ${comp.value >= 7 ? 'text-emerald-400' : comp.value >= 4 ? 'text-[#C9A64A]' : 'text-red-400'}`}>{comp.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // V/TO View
  const VTOView = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-500/10 to-[#C9A64A]/10 border border-purple-500/30 rounded-2xl p-4">
        <h3 className="text-lg font-bold text-white">Vision/Traction Organizer™</h3>
        <p className="text-gray-400 text-sm">The 2-page strategic document that aligns your entire organization</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Core Values */}
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
          <h4 className="text-[#C9A64A] font-bold mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#C9A64A]/20 flex items-center justify-center text-xs">1</span>
            Core Values (3-7 values)
          </h4>
          {vto.coreValues.map((val, idx) => (
            <input key={idx} type="text" value={val} placeholder={`Core Value ${idx + 1}`}
              onChange={(e) => { const newVals = [...vto.coreValues]; newVals[idx] = e.target.value; setVto({...vto, coreValues: newVals}); }}
              className="w-full mb-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm" />
          ))}
          <button onClick={() => setVto({...vto, coreValues: [...vto.coreValues, '']})} className="text-[#C9A64A] text-sm hover:underline">+ Add Value</button>
        </div>

        {/* Core Focus */}
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
          <h4 className="text-[#C9A64A] font-bold mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#C9A64A]/20 flex items-center justify-center text-xs">2</span>
            Core Focus™
          </h4>
          <label className="text-gray-400 text-xs uppercase mb-1 block">Purpose/Cause/Passion</label>
          <input type="text" value={vto.coreFocus.purpose} placeholder="Why do you exist?"
            onChange={(e) => setVto({...vto, coreFocus: {...vto.coreFocus, purpose: e.target.value}})}
            className="w-full mb-3 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm" />
          <label className="text-gray-400 text-xs uppercase mb-1 block">Niche</label>
          <input type="text" value={vto.coreFocus.niche} placeholder="What do you do best?"
            onChange={(e) => setVto({...vto, coreFocus: {...vto.coreFocus, niche: e.target.value}})}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm" />
        </div>

        {/* 10-Year Target */}
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
          <h4 className="text-[#C9A64A] font-bold mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#C9A64A]/20 flex items-center justify-center text-xs">3</span>
            10-Year Target™
          </h4>
          <textarea value={vto.tenYearTarget} placeholder="What's your big, long-term goal?"
            onChange={(e) => setVto({...vto, tenYearTarget: e.target.value})}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm h-24 resize-none" />
        </div>

        {/* 3-Year Picture */}
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
          <h4 className="text-[#C9A64A] font-bold mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#C9A64A]/20 flex items-center justify-center text-xs">4</span>
            3-Year Picture™
          </h4>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input type="text" value={vto.threeYearPicture.revenue} placeholder="Revenue"
              onChange={(e) => setVto({...vto, threeYearPicture: {...vto.threeYearPicture, revenue: e.target.value}})}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm" />
            <input type="text" value={vto.threeYearPicture.profit} placeholder="Profit"
              onChange={(e) => setVto({...vto, threeYearPicture: {...vto.threeYearPicture, profit: e.target.value}})}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm" />
          </div>
          <input type="text" value={vto.threeYearPicture.measurables} placeholder="Key measurables"
            onChange={(e) => setVto({...vto, threeYearPicture: {...vto.threeYearPicture, measurables: e.target.value}})}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm" />
        </div>

        {/* 1-Year Plan */}
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10 lg:col-span-2">
          <h4 className="text-[#C9A64A] font-bold mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#C9A64A]/20 flex items-center justify-center text-xs">5</span>
            1-Year Plan
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-gray-400 text-xs uppercase mb-1 block">Revenue Goal</label>
              <input type="text" value={vto.oneYearPlan.revenue} placeholder="$"
                onChange={(e) => setVto({...vto, oneYearPlan: {...vto.oneYearPlan, revenue: e.target.value}})}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm" />
            </div>
            <div>
              <label className="text-gray-400 text-xs uppercase mb-1 block">Profit Goal</label>
              <input type="text" value={vto.oneYearPlan.profit} placeholder="$"
                onChange={(e) => setVto({...vto, oneYearPlan: {...vto.oneYearPlan, profit: e.target.value}})}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm" />
            </div>
            <div>
              <label className="text-gray-400 text-xs uppercase mb-1 block">Goals (3-7)</label>
              {vto.oneYearPlan.goals.map((goal, idx) => (
                <input key={idx} type="text" value={goal} placeholder={`Goal ${idx + 1}`}
                  onChange={(e) => { const newGoals = [...vto.oneYearPlan.goals]; newGoals[idx] = e.target.value; setVto({...vto, oneYearPlan: {...vto.oneYearPlan, goals: newGoals}}); }}
                  className="w-full mb-1 px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-xs" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Rocks View
  const RocksView = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-500/10 to-[#C9A64A]/10 border border-emerald-500/30 rounded-2xl p-4">
        <h3 className="text-lg font-bold text-white">Quarterly Rocks</h3>
        <p className="text-gray-400 text-sm">90-day priorities - the 3-7 most important things that must get done</p>
      </div>

      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F1A2A]/80">
            <tr>
              <th className="px-4 py-3 text-left text-[#C9A64A] font-bold text-xs uppercase">Owner</th>
              <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase">Rock</th>
              <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase">Due</th>
              <th className="px-4 py-3 text-left text-white font-bold text-xs uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rocks.map((rock, idx) => (
              <tr key={rock.id} className="hover:bg-white/5">
                <td className="px-4 py-3">
                  <input type="text" value={rock.owner} placeholder="Name"
                    onChange={(e) => { const newRocks = [...rocks]; newRocks[idx].owner = e.target.value; setRocks(newRocks); }}
                    className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white placeholder-gray-500 text-sm" />
                </td>
                <td className="px-4 py-3">
                  <input type="text" value={rock.rock} placeholder="Describe the rock..."
                    onChange={(e) => { const newRocks = [...rocks]; newRocks[idx].rock = e.target.value; setRocks(newRocks); }}
                    className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white placeholder-gray-500 text-sm" />
                </td>
                <td className="px-4 py-3">
                  <input type="date" value={rock.dueDate}
                    onChange={(e) => { const newRocks = [...rocks]; newRocks[idx].dueDate = e.target.value; setRocks(newRocks); }}
                    className="px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm" />
                </td>
                <td className="px-4 py-3">
                  <select value={rock.status}
                    onChange={(e) => { const newRocks = [...rocks]; newRocks[idx].status = e.target.value; setRocks(newRocks); }}
                    className={`px-2 py-1 rounded text-sm font-semibold ${
                      rock.status === 'done' ? 'bg-emerald-500/20 text-emerald-400' :
                      rock.status === 'off-track' ? 'bg-red-500/20 text-red-400' :
                      'bg-[#C9A64A]/20 text-[#C9A64A]'
                    }`}>
                    <option value="on-track">On Track</option>
                    <option value="off-track">Off Track</option>
                    <option value="done">Done</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => setRocks([...rocks, { id: rocks.length + 1, owner: '', rock: '', dueDate: '', status: 'on-track' }])}
        className="px-4 py-2 bg-[#C9A64A]/20 text-[#C9A64A] rounded-lg text-sm font-semibold hover:bg-[#C9A64A]/30 transition-colors">
        + Add Rock
      </button>
    </div>
  );

  // L10 Meeting View
  const L10View = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-500/10 to-[#C9A64A]/10 border border-blue-500/30 rounded-2xl p-4">
        <h3 className="text-lg font-bold text-white">Level 10 Meeting™ Agenda</h3>
        <p className="text-gray-400 text-sm">Weekly 90-minute leadership meeting - same day, same time, every week</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Segue */}
        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded">5 min</span>
            <h4 className="text-white font-semibold">Segue</h4>
          </div>
          <textarea value={l10.segue} placeholder="Share good news (personal & professional)"
            onChange={(e) => setL10({...l10, segue: e.target.value})}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm h-16 resize-none" />
        </div>

        {/* Scorecard */}
        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded">5 min</span>
            <h4 className="text-white font-semibold">Scorecard Review</h4>
          </div>
          <textarea value={l10.scorecardNotes} placeholder="Review weekly numbers - any off track?"
            onChange={(e) => setL10({...l10, scorecardNotes: e.target.value})}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm h-16 resize-none" />
        </div>

        {/* Rock Review */}
        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-[#C9A64A]/20 text-[#C9A64A] text-xs font-bold rounded">5 min</span>
            <h4 className="text-white font-semibold">Rock Review</h4>
          </div>
          <textarea value={l10.rockReview} placeholder="On track or off track? Quick status updates."
            onChange={(e) => setL10({...l10, rockReview: e.target.value})}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm h-16 resize-none" />
        </div>

        {/* Headlines */}
        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded">5 min</span>
            <h4 className="text-white font-semibold">Customer/Employee Headlines</h4>
          </div>
          <textarea value={l10.headlines} placeholder="Any customer or employee news to share?"
            onChange={(e) => setL10({...l10, headlines: e.target.value})}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm h-16 resize-none" />
        </div>

        {/* To-Do List */}
        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs font-bold rounded">5 min</span>
            <h4 className="text-white font-semibold">To-Do List (7-day action items)</h4>
          </div>
          {l10.todos.map((todo, idx) => (
            <input key={idx} type="text" value={todo} placeholder={`To-do ${idx + 1}`}
              onChange={(e) => { const newTodos = [...l10.todos]; newTodos[idx] = e.target.value; setL10({...l10, todos: newTodos}); }}
              className="w-full mb-1 px-3 py-1 bg-white/5 border border-white/10 rounded text-white placeholder-gray-500 text-sm" />
          ))}
        </div>

        {/* IDS */}
        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded">60 min</span>
            <h4 className="text-white font-semibold">IDS™ (Identify, Discuss, Solve)</h4>
          </div>
          {l10.issuesList.map((issue, idx) => (
            <input key={idx} type="text" value={issue} placeholder={`Issue ${idx + 1}`}
              onChange={(e) => { const newIssues = [...l10.issuesList]; newIssues[idx] = e.target.value; setL10({...l10, issuesList: newIssues}); }}
              className="w-full mb-1 px-3 py-1 bg-white/5 border border-white/10 rounded text-white placeholder-gray-500 text-sm" />
          ))}
        </div>
      </div>
    </div>
  );

  // Scorecard View
  const ScorecardView = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-500/10 to-[#C9A64A]/10 border border-orange-500/30 rounded-2xl p-4">
        <h3 className="text-lg font-bold text-white">Weekly Scorecard</h3>
        <p className="text-gray-400 text-sm">5-15 weekly numbers that tell you if you&apos;re on track</p>
      </div>

      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0F1A2A]/80">
            <tr>
              <th className="px-3 py-3 text-left text-[#C9A64A] font-bold text-xs uppercase">Measurable</th>
              <th className="px-3 py-3 text-left text-white font-bold text-xs uppercase">Owner</th>
              <th className="px-3 py-3 text-left text-white font-bold text-xs uppercase">Goal</th>
              <th className="px-3 py-3 text-center text-white font-bold text-xs uppercase">W1</th>
              <th className="px-3 py-3 text-center text-white font-bold text-xs uppercase">W2</th>
              <th className="px-3 py-3 text-center text-white font-bold text-xs uppercase">W3</th>
              <th className="px-3 py-3 text-center text-white font-bold text-xs uppercase">W4</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {scorecard.map((row, idx) => (
              <tr key={row.id} className="hover:bg-white/5">
                <td className="px-3 py-2">
                  <input type="text" value={row.measurable} placeholder="KPI"
                    onChange={(e) => { const newSc = [...scorecard]; newSc[idx].measurable = e.target.value; setScorecard(newSc); }}
                    className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm" />
                </td>
                <td className="px-3 py-2">
                  <input type="text" value={row.owner} placeholder="Name"
                    onChange={(e) => { const newSc = [...scorecard]; newSc[idx].owner = e.target.value; setScorecard(newSc); }}
                    className="w-20 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm" />
                </td>
                <td className="px-3 py-2">
                  <input type="text" value={row.goal} placeholder="Target"
                    onChange={(e) => { const newSc = [...scorecard]; newSc[idx].goal = e.target.value; setScorecard(newSc); }}
                    className="w-16 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm" />
                </td>
                {['w1', 'w2', 'w3', 'w4'].map((week) => (
                  <td key={week} className="px-3 py-2 text-center">
                    <input type="text" value={row[week]} placeholder="-"
                      onChange={(e) => { const newSc = [...scorecard]; newSc[idx][week] = e.target.value; setScorecard(newSc); }}
                      className="w-12 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm text-center" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => setScorecard([...scorecard, { id: scorecard.length + 1, measurable: '', owner: '', goal: '', w1: '', w2: '', w3: '', w4: '' }])}
        className="px-4 py-2 bg-[#C9A64A]/20 text-[#C9A64A] rounded-lg text-sm font-semibold hover:bg-[#C9A64A]/30 transition-colors">
        + Add Measurable
      </button>
    </div>
  );

  // People Analyzer View
  const PeopleAnalyzerView = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-pink-500/10 to-[#C9A64A]/10 border border-pink-500/30 rounded-2xl p-4">
        <h3 className="text-lg font-bold text-white">People Analyzer™</h3>
        <p className="text-gray-400 text-sm">Evaluate: Right Person (Core Values) + Right Seat (GWC)</p>
      </div>

      {/* Core Values Setup */}
      <div className="bg-white/5 p-4 rounded-xl border border-white/10">
        <h4 className="text-[#C9A64A] font-semibold mb-3">Your Core Values</h4>
        <div className="flex flex-wrap gap-2">
          {peopleAnalyzer.coreValues.map((val, idx) => (
            <input key={idx} type="text" value={val} placeholder={`Value ${idx + 1}`}
              onChange={(e) => { const newVals = [...peopleAnalyzer.coreValues]; newVals[idx] = e.target.value; setPeopleAnalyzer({...peopleAnalyzer, coreValues: newVals}); }}
              className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 text-sm w-32" />
          ))}
        </div>
      </div>

      {/* Team Evaluation */}
      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0F1A2A]/80">
            <tr>
              <th className="px-3 py-3 text-left text-white font-bold text-xs uppercase">Name</th>
              <th className="px-3 py-3 text-left text-white font-bold text-xs uppercase">Role</th>
              <th className="px-3 py-3 text-center text-[#C9A64A] font-bold text-xs uppercase" colSpan="3">Core Values (+/- each)</th>
              <th className="px-3 py-3 text-center text-emerald-400 font-bold text-xs uppercase" colSpan="3">GWC (Yes/No)</th>
            </tr>
            <tr className="bg-[#0F1A2A]/60">
              <th></th>
              <th></th>
              {peopleAnalyzer.coreValues.slice(0, 3).map((val, idx) => (
                <th key={idx} className="px-2 py-1 text-center text-gray-400 font-normal text-xs">{val.slice(0, 6)}</th>
              ))}
              <th className="px-2 py-1 text-center text-gray-400 font-normal text-xs">Gets It</th>
              <th className="px-2 py-1 text-center text-gray-400 font-normal text-xs">Wants It</th>
              <th className="px-2 py-1 text-center text-gray-400 font-normal text-xs">Capacity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {peopleAnalyzer.team.map((person, idx) => (
              <tr key={idx} className="hover:bg-white/5">
                <td className="px-3 py-2">
                  <input type="text" value={person.name} placeholder="Name"
                    onChange={(e) => { const newTeam = [...peopleAnalyzer.team]; newTeam[idx].name = e.target.value; setPeopleAnalyzer({...peopleAnalyzer, team: newTeam}); }}
                    className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm" />
                </td>
                <td className="px-3 py-2">
                  <input type="text" value={person.role} placeholder="Seat"
                    onChange={(e) => { const newTeam = [...peopleAnalyzer.team]; newTeam[idx].role = e.target.value; setPeopleAnalyzer({...peopleAnalyzer, team: newTeam}); }}
                    className="w-24 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm" />
                </td>
                {['v1', 'v2', 'v3'].map((v) => (
                  <td key={v} className="px-2 py-2 text-center">
                    <select value={person[v]}
                      onChange={(e) => { const newTeam = [...peopleAnalyzer.team]; newTeam[idx][v] = Number(e.target.value); setPeopleAnalyzer({...peopleAnalyzer, team: newTeam}); }}
                      className={`px-1 py-1 rounded text-sm ${person[v] === 1 ? 'bg-emerald-500/20 text-emerald-400' : person[v] === -1 ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-gray-400'}`}>
                      <option value={0}>±</option>
                      <option value={1}>+</option>
                      <option value={-1}>-</option>
                    </select>
                  </td>
                ))}
                {['getsIt', 'wantsIt', 'capacity'].map((gwc) => (
                  <td key={gwc} className="px-2 py-2 text-center">
                    <button onClick={() => { const newTeam = [...peopleAnalyzer.team]; newTeam[idx][gwc] = !newTeam[idx][gwc]; setPeopleAnalyzer({...peopleAnalyzer, team: newTeam}); }}
                      className={`w-8 h-8 rounded ${person[gwc] ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-gray-500'}`}>
                      {person[gwc] ? 'Y' : 'N'}
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => setPeopleAnalyzer({...peopleAnalyzer, team: [...peopleAnalyzer.team, { name: '', role: '', v1: 0, v2: 0, v3: 0, getsIt: false, wantsIt: false, capacity: false }]})}
        className="px-4 py-2 bg-[#C9A64A]/20 text-[#C9A64A] rounded-lg text-sm font-semibold hover:bg-[#C9A64A]/30 transition-colors">
        + Add Person
      </button>
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white mb-2">EOS® Toolkit</h2>
        <p className="text-gray-400">Entrepreneurial Operating System® tools by Gino Wickman</p>
      </div>

      {/* Tool Navigation */}
      <div className="flex flex-wrap gap-2 mb-8 p-1 bg-white/5 rounded-xl">
        {tools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => setActiveView(tool.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
              activeView === tool.id 
                ? 'bg-[#C9A64A] text-[#0F1A2A]' 
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            {tool.icon}
            {tool.name}
          </button>
        ))}
      </div>

      {/* Render Active View */}
      {activeView === 'health' && <HealthCheckView />}
      {activeView === 'vto' && <VTOView />}
      {activeView === 'rocks' && <RocksView />}
      {activeView === 'l10' && <L10View />}
      {activeView === 'scorecard' && <ScorecardView />}
      {activeView === 'people' && <PeopleAnalyzerView />}

      {/* Book Reference */}
      <div className="mt-8 bg-gradient-to-r from-[#C9A64A]/5 to-purple-500/5 border border-[#C9A64A]/20 rounded-2xl p-6">
        <div className="flex items-center gap-4">
          <div className="text-4xl">📖</div>
          <div>
            <h4 className="text-lg font-bold text-white">Learn EOS®</h4>
            <p className="text-gray-400">Read <span className="text-[#C9A64A] font-semibold">&ldquo;Traction&rdquo;</span> by Gino Wickman — 1M+ copies sold, 100K+ companies running on EOS®</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- 13. Trevor Blackwell Startup Growth Calculator ---
const StartupGrowthCalculator = () => {
  const [initialRevenue, setInitialRevenue] = useState(10000);
  const [initialExpenses, setInitialExpenses] = useState(50000);
  const [revenueGrowthRate, setRevenueGrowthRate] = useState(7);
  const [period, setPeriod] = useState('weekly');
  const [showProjection, setShowProjection] = useState(false);
  
  // Convert to weekly if needed for calculation
  const getWeeklyValues = () => {
    if (period === 'monthly') {
      return {
        revenue: initialRevenue / 4.33,
        expenses: initialExpenses / 4.33,
        growth: Math.pow(1 + revenueGrowthRate / 100, 1/4.33) - 1
      };
    } else if (period === 'yearly') {
      return {
        revenue: initialRevenue / 52,
        expenses: initialExpenses / 52,
        growth: Math.pow(1 + revenueGrowthRate / 100, 1/52) - 1
      };
    }
    return {
      revenue: initialRevenue,
      expenses: initialExpenses,
      growth: revenueGrowthRate / 100
    };
  };

  const calculateProjection = () => {
    const { revenue, expenses, growth } = getWeeklyValues();
    let currentRevenue = revenue;
    let cumulativeLoss = 0;
    let maxCapitalRequired = 0;
    let weeksToProfit = 0;
    const projection = [];
    
    // Project up to 260 weeks (5 years)
    for (let week = 1; week <= 260; week++) {
      currentRevenue = currentRevenue * (1 + growth);
      const weeklyProfit = currentRevenue - expenses;
      cumulativeLoss += weeklyProfit < 0 ? weeklyProfit : 0;
      
      if (Math.abs(cumulativeLoss) > maxCapitalRequired) {
        maxCapitalRequired = Math.abs(cumulativeLoss);
      }
      
      projection.push({
        week,
        revenue: currentRevenue,
        expenses,
        profit: weeklyProfit,
        cumulativeLoss: Math.abs(cumulativeLoss)
      });
      
      if (currentRevenue >= expenses && weeksToProfit === 0) {
        weeksToProfit = week;
      }
    }
    
    return {
      weeksToProfit: weeksToProfit || 'Beyond 5 years',
      monthsToProfit: weeksToProfit ? (weeksToProfit / 4.33).toFixed(1) : 'Beyond 5 years',
      maxCapitalRequired,
      projection: projection.filter((_, i) => i % 4 === 0).slice(0, 24) // Show monthly for 2 years
    };
  };

  const results = calculateProjection();
  const isProfitable = typeof results.weeksToProfit === 'number';

  // Calculate hiring insights based on growth rate
  const engineersNeeded = revenueGrowthRate >= 7 ? Math.ceil(revenueGrowthRate / 3.5) : 1;
  const avgEngineerCost = 12000; // Monthly fully-loaded cost
  const hiringCostImpact = engineersNeeded * avgEngineerCost;

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <SectionHeader 
          title="Startup Growth Calculator" 
          description="The Trevor Blackwell model: Project path to profitability based on growth rate."
        />
        <div className="flex items-center gap-2 px-4 py-2 bg-[#C9A64A]/10 rounded-xl border border-[#C9A64A]/30">
          <span className="text-[#C9A64A] text-sm font-semibold">🚀 YC Classic Tool</span>
        </div>
      </div>

      {/* YC Philosophy Banner */}
      <div className="mb-8 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-orange-500/20">
            <TrendingUp className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white mb-2">The YC Philosophy: &ldquo;Grow Fast&rdquo;</h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              This calculator shows the power of <span className="text-orange-400 font-semibold">compounding growth</span>. 
              Even small increases in weekly growth rate (e.g., from 5% to 7%) dramatically reduce the time and capital needed to reach profitability. 
              YC&apos;s mantra is simple: <span className="text-[#C9A64A] font-semibold">sustainable, fast growth wins</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Key Output Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card 
          title="Time to Profitability" 
          value={isProfitable ? `${results.monthsToProfit} Months` : 'N/A'} 
          subtext={isProfitable ? `${results.weeksToProfit} weeks` : 'Growth rate too low'}
          icon={Clock} 
          colorClass={isProfitable ? "from-emerald-400 to-emerald-600" : "from-red-400 to-red-600"} 
        />
        <Card 
          title="Max Capital Required" 
          value={fmtCurrency(results.maxCapitalRequired)} 
          subtext="Total burn before breakeven"
          icon={DollarSign} 
          colorClass="from-[#C9A64A] to-[#D4B76A]" 
        />
        <Card 
          title="Hiring Insight" 
          value={`${engineersNeeded} Eng/Month`} 
          subtext={`+${fmtCurrency(hiringCostImpact)}/mo to sustain ${revenueGrowthRate}% growth`}
          icon={Users} 
          colorClass="from-purple-400 to-purple-600" 
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Input Panel */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 shadow-xl">
            <h3 className="font-bold mb-6 text-lg text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#C9A64A]" />
              Assumptions
            </h3>
            
            {/* Period Selector */}
            <div className="mb-6">
              <label className="block text-xs font-bold uppercase text-[#C9A64A] mb-3 tracking-widest">Time Period</label>
              <div className="grid grid-cols-3 gap-2">
                {['weekly', 'monthly', 'yearly'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                      period === p 
                        ? 'bg-[#C9A64A] text-[#0F1A2A]' 
                        : 'bg-white/5 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <InputGroup 
              label={`Initial Revenue (${period})`} 
              value={initialRevenue} 
              onChange={setInitialRevenue} 
              prefix="$" 
            />
            <InputGroup 
              label={`Expenses (${period})`} 
              value={initialExpenses} 
              onChange={setInitialExpenses} 
              prefix="$" 
            />
            
            <div className="mb-5">
              <label className="block text-xs font-bold uppercase text-[#C9A64A] mb-2 tracking-widest">
                Revenue Growth Rate ({period})
              </label>
              <input 
                type="range"
                value={revenueGrowthRate} 
                onChange={(e) => setRevenueGrowthRate(Number(e.target.value))}
                min="1" max="20" step="0.5"
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#C9A64A]"
              />
              <div className="flex justify-between mt-2">
                <span className="text-gray-400 text-xs">1%</span>
                <span className="text-[#C9A64A] text-lg font-bold">{revenueGrowthRate}%</span>
                <span className="text-gray-400 text-xs">20%</span>
              </div>
            </div>

            {/* Growth Rate Benchmarks */}
            <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-semibold">YC Growth Benchmarks</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Good</span>
                  <span className="text-yellow-400 font-mono">5-7% weekly</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Great</span>
                  <span className="text-emerald-400 font-mono">10% weekly</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Exceptional</span>
                  <span className="text-[#C9A64A] font-mono">15%+ weekly</span>
                </div>
              </div>
            </div>
          </div>

          {/* AI-Powered Hiring Insight */}
          <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 backdrop-blur-xl p-6 rounded-2xl border border-purple-500/30 shadow-xl">
            <h3 className="font-bold mb-4 text-lg text-white flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-purple-400" />
              Hiring Cost Integration
            </h3>
            <p className="text-gray-300 text-sm mb-4">
              To sustain <span className="text-[#C9A64A] font-bold">{revenueGrowthRate}% {period}</span> growth:
            </p>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 text-sm">Engineers needed</span>
                <span className="text-white font-bold">{engineersNeeded}/month</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 text-sm">Additional burn</span>
                <span className="text-purple-400 font-bold">{fmtCurrency(hiringCostImpact)}/mo</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                <span className="text-gray-400 text-sm">Adjusted expenses</span>
                <span className="text-[#C9A64A] font-bold">{fmtCurrency(initialExpenses + (period === 'monthly' ? hiringCostImpact : hiringCostImpact * (period === 'weekly' ? 0.25 : 12)))}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Projection Table */}
        <div className="lg:col-span-2 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-xl">
          <div className="p-6 border-b border-white/10 flex justify-between items-center">
            <h3 className="font-bold text-lg text-white">24-Month Projection</h3>
            <button 
              onClick={() => setShowProjection(!showProjection)}
              className="text-sm text-[#C9A64A] hover:underline font-semibold"
            >
              {showProjection ? 'Hide Details' : 'Show Details'}
            </button>
          </div>
          
          {showProjection && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0F1A2A]/80 border-b border-white/10">
                  <tr>
                    <th className="px-4 py-4 text-left text-[#C9A64A] font-bold uppercase text-xs tracking-wider">Month</th>
                    <th className="px-4 py-4 text-left text-white font-bold uppercase text-xs">Revenue</th>
                    <th className="px-4 py-4 text-left text-white font-bold uppercase text-xs">Expenses</th>
                    <th className="px-4 py-4 text-left text-white font-bold uppercase text-xs">Profit/Loss</th>
                    <th className="px-4 py-4 text-left text-white font-bold uppercase text-xs">Cumulative Burn</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {results.projection.map((row, idx) => (
                    <tr key={idx} className={`hover:bg-white/5 transition-colors ${row.profit >= 0 ? 'bg-emerald-500/5' : ''}`}>
                      <td className="px-4 py-3 font-bold text-[#C9A64A]">M{idx + 1}</td>
                      <td className="px-4 py-3 text-white font-mono">{fmtCurrency(row.revenue * 4.33)}</td>
                      <td className="px-4 py-3 text-gray-300 font-mono">{fmtCurrency(row.expenses * 4.33)}</td>
                      <td className={`px-4 py-3 font-bold font-mono ${row.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {fmtCurrency(row.profit * 4.33)}
                      </td>
                      <td className="px-4 py-3 text-orange-400 font-mono">{fmtCurrency(row.cumulativeLoss)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!showProjection && (
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#C9A64A]/10 mb-6">
                <TrendingUp className="w-10 h-10 text-[#C9A64A]" />
              </div>
              <h4 className="text-2xl font-bold text-white mb-2">
                {isProfitable ? `Profitable in ${results.monthsToProfit} months` : 'Increase growth rate'}
              </h4>
              <p className="text-gray-400 mb-6">
                {isProfitable 
                  ? `You'll need ${fmtCurrency(results.maxCapitalRequired)} in capital to reach profitability`
                  : 'Current growth rate is insufficient to reach profitability within 5 years'
                }
              </p>
              <button 
                onClick={() => setShowProjection(true)}
                className="px-6 py-3 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] rounded-xl font-bold hover:scale-105 transition-all duration-300 shadow-lg"
              >
                View Full Projection
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Insights */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
          <h3 className="text-lg font-bold text-white mb-3">📊 How This Calculator Works</h3>
          <ul className="text-gray-300 space-y-2 text-sm">
            <li>• Projects revenue growth using compound interest formula</li>
            <li>• Assumes flat expenses (or integrate hiring costs above)</li>
            <li>• Calculates weeks until revenue exceeds expenses</li>
            <li>• Tracks maximum capital needed before breakeven</li>
          </ul>
        </div>
        <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
          <h3 className="text-lg font-bold text-white mb-3">💡 Key Insights</h3>
          <ul className="text-gray-300 space-y-2 text-sm">
            <li>• <span className="text-[#C9A64A]">7% weekly growth</span> = 33x annual growth</li>
            <li>• Doubling growth rate can halve time to profitability</li>
            <li>• Small improvements in retention compound dramatically</li>
            <li>• Factor in hiring costs for realistic projections</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// --- 12. Board Deck Presentation ---
const BoardPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [companyName, setCompanyName] = useState('Virtual CFO');
  const [date, setDate] = useState(new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));

  const slides = [
    {
      id: 0,
      type: 'cover',
      title: 'Board Deck',
      subtitle: 'Quarterly Review',
    },
    {
      id: 1,
      type: 'content',
      title: 'Executive Summary',
      content: [
        { label: 'Revenue', value: '$2.4M ARR', change: '+42% YoY', positive: true },
        { label: 'Burn Rate', value: '$185K/mo', change: '-12% MoM', positive: true },
        { label: 'Runway', value: '14 months', change: '+2 months', positive: true },
        { label: 'Customers', value: '847', change: '+124 QoQ', positive: true },
      ]
    },
    {
      id: 2,
      type: 'content',
      title: 'Key Metrics',
      content: [
        { label: 'CAC', value: '$2,400', change: '-8%', positive: true },
        { label: 'LTV', value: '$12,800', change: '+15%', positive: true },
        { label: 'LTV/CAC', value: '5.3x', change: '+0.8x', positive: true },
        { label: 'NRR', value: '118%', change: '+5%', positive: true },
      ]
    },
    {
      id: 3,
      type: 'content',
      title: 'Financial Highlights',
      content: [
        { label: 'Monthly Revenue', value: '$200K', change: '+35%', positive: true },
        { label: 'Gross Margin', value: '82%', change: '+3%', positive: true },
        { label: 'Operating Margin', value: '-15%', change: '+8%', positive: true },
        { label: 'Cash Balance', value: '$2.6M', change: '+$400K', positive: true },
      ]
    },
  ];

  const currentSlideData = slides[currentSlide];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div>
      <SectionHeader title="Board Deck Presentation" description="Professional quarterly board presentation template" />
      
      {/* Customization Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 shadow-xl">
          <label className="block text-xs font-bold uppercase text-[#C9A64A] mb-2 tracking-widest">Company Name</label>
          <input 
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#C9A64A] focus:border-[#C9A64A] outline-none text-white transition-all duration-300"
          />
        </div>
        <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 shadow-xl">
          <label className="block text-xs font-bold uppercase text-[#C9A64A] mb-2 tracking-widest">Date</label>
          <input 
            type="text"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#C9A64A] focus:border-[#C9A64A] outline-none text-white transition-all duration-300"
          />
        </div>
      </div>

      {/* Presentation Slide */}
      <div className="relative">
        <div className="bg-gradient-to-br from-[#0F1A2A] via-[#1A1A1C] to-[#0F1A2A] rounded-3xl shadow-2xl border border-white/10 overflow-hidden" style={{ aspectRatio: '16/9' }}>
          
          {currentSlideData.type === 'cover' ? (
            // Cover Slide
            <div className="h-full flex flex-col items-center justify-center p-16 text-center relative">
              <div className="absolute inset-0 opacity-5 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&q=85)' }} />
              <div className="relative z-10 space-y-8">
                <div className="text-2xl font-bold tracking-[0.3em] text-[#C9A64A] uppercase mb-12">
                  {companyName}
                </div>
                <h1 className="text-7xl font-bold text-white mb-6 tracking-tight">
                  {currentSlideData.title}
                </h1>
                <h2 className="text-4xl font-light text-gray-300 mb-12">
                  {currentSlideData.subtitle}
                </h2>
                <div className="inline-block px-8 py-4 border-2 border-white/30 rounded-lg text-xl text-white">
                  {date}
                </div>
              </div>
              <div className="absolute bottom-8 left-16 right-16 flex items-center justify-between text-gray-400 text-sm">
                <div className="font-semibold">{companyName}</div>
                <div className="font-semibold">{currentSlide + 1}/{slides.length}</div>
              </div>
            </div>
          ) : (
            // Content Slide
            <div className="h-full flex flex-col p-16 relative">
              <div className="absolute top-8 left-16 text-[#C9A64A] text-sm font-bold tracking-[0.2em] uppercase">
                {companyName}
              </div>
              <div className="absolute top-8 right-16 text-gray-400 text-sm font-semibold">
                {currentSlide + 1}/{slides.length}
              </div>
              
              <div className="flex-1 flex flex-col justify-center mt-12">
                <h2 className="text-5xl font-bold text-white mb-12 tracking-tight">
                  {currentSlideData.title}
                </h2>
                
                <div className="grid grid-cols-2 gap-8">
                  {currentSlideData.content.map((item, index) => (
                    <div key={index} className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10 hover:border-[#C9A64A]/50 transition-all duration-300">
                      <div className="text-sm text-gray-400 uppercase tracking-wider mb-3 font-semibold">
                        {item.label}
                      </div>
                      <div className="text-4xl font-bold text-white mb-3">
                        {item.value}
                      </div>
                      <div className={`text-lg font-semibold ${item.positive ? 'text-emerald-400' : 'text-red-400'}`}>
                        {item.change}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center justify-center gap-6 mt-8">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="px-6 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-white font-semibold hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300"
          >
            ← Previous
          </button>
          
          <div className="flex items-center gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-[#C9A64A] w-8' 
                    : 'bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="px-6 py-3 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] rounded-xl font-bold hover:scale-105 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-8 bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10">
        <h3 className="text-lg font-bold text-white mb-3">💡 Presentation Tips</h3>
        <ul className="text-gray-300 space-y-2 text-sm">
          <li>• Customize company name and date to personalize your board deck</li>
          <li>• Use arrow keys or navigation buttons to move between slides</li>
          <li>• This template includes cover and key metric slides for quarterly reviews</li>
          <li>• Present in fullscreen mode for best viewing experience</li>
        </ul>
      </div>
    </div>
  );
};

// --- Main App Component ---
export default function FPAToolkit() {
  const [activeTab, setActiveTab] = useState(null); // null = show tiles view
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const tools = [
    { id: 'eos', name: 'EOS® Toolkit', icon: Layers, description: 'Complete EOS® tools: V/TO, Rocks, L10 Meeting, Scorecard, People Analyzer', tag: 'NEW' },
    { id: 'saasval', name: 'SaaS Valuation Calculator', icon: DollarSign, description: 'Calculate your SaaS company valuation using industry-standard formulas' },
    { id: 'growth', name: 'Startup Growth Calculator', icon: TrendingUp, description: 'The Trevor Blackwell model - Project path to profitability based on growth rate', tag: 'YC Classic' },
    { id: 'saas', name: 'SaaS Projection Model', icon: Activity, description: 'Forecast 12-month growth, revenue, churn, and burn for SaaS businesses' },
    { id: 'cac', name: 'CAC Payback Period', icon: Users, description: 'Calculate months required to recover customer acquisition cost' },
    { id: 'burn', name: 'Burn Rate & Runway', icon: TrendingDown, description: 'Calculate how long until you run out of cash' },
    { id: 'ltv', name: 'LTV Calculator', icon: DollarSign, description: 'Estimate total gross profit from a single customer' },
    { id: 'rule40', name: 'Rule of 40', icon: Percent, description: 'SaaS health metric - Growth % + Profit % should be > 40' },
    { id: 'headcount', name: 'Headcount Cost Planner', icon: Briefcase, description: 'Estimate fully loaded workforce costs with benefits and taxes' },
    { id: 'breakeven', name: 'Break-even Analysis', icon: Layers, description: 'Determine sales volume needed to cover costs' },
    { id: 'cagr', name: 'CAGR Calculator', icon: TrendingUp, description: 'Calculate Compound Annual Growth Rate' },
    { id: 'workingcap', name: 'Working Capital', icon: BarChart2, description: 'Assess liquidity and short-term financial health' },
    { id: 'dcf', name: 'DCF Valuation', icon: Calculator, description: 'Estimate value based on future cash flows' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'eos': return <EOSCalculator />;
      case 'saasval': return <SaaSValuationCalculator />;
      case 'growth': return <StartupGrowthCalculator />;
      case 'saas': return <SaaSModel />;
      case 'cac': return <CACPayback />;
      case 'burn': return <BurnRunway />;
      case 'ltv': return <LTVCalculator />;
      case 'rule40': return <RuleOf40 />;
      case 'headcount': return <HeadcountPlanner />;
      case 'breakeven': return <BreakEven />;
      case 'cagr': return <CAGRCalc />;
      case 'workingcap': return <WorkingCapital />;
      case 'dcf': return <DCFModel />;
      default: return null;
    }
  };

  // Tiles Landing View
  const TilesView = () => (
    <div>
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#C9A64A] to-[#D4B76A] shadow-xl mb-6">
          <PieChart className="w-10 h-10 text-[#0F1A2A]" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">FP&A Toolkit</h1>
        <p className="text-xl text-gray-300 max-w-2xl mx-auto">
          Professional financial planning and analysis tools for startups and growing businesses
        </p>
      </div>

      {/* Calculator Tiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => setActiveTab(tool.id)}
            className="group text-left bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 hover:border-[#C9A64A]/50 hover:bg-white/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#C9A64A]/10"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#C9A64A]/20 to-[#D4B76A]/20 group-hover:from-[#C9A64A] group-hover:to-[#D4B76A] transition-all duration-300">
                <tool.icon className="w-6 h-6 text-[#C9A64A] group-hover:text-[#0F1A2A] transition-colors duration-300" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-bold text-white group-hover:text-[#C9A64A] transition-colors">
                    {tool.name}
                  </h3>
                  {tool.tag && (
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-orange-500/20 text-orange-400 rounded-full">
                      {tool.tag}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {tool.description}
                </p>
              </div>
            </div>
          </button>
        ))}

        {/* Special Tile for Headcount & Payroll Planner */}
        <Link 
          to="/calculators/hiring-planner"
          className="group text-left bg-gradient-to-br from-[#C9A64A]/10 to-[#D4B76A]/5 backdrop-blur-xl p-6 rounded-2xl border border-[#C9A64A]/30 hover:border-[#C9A64A] hover:bg-[#C9A64A]/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#C9A64A]/20"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#C9A64A] to-[#D4B76A]">
              <Users className="w-6 h-6 text-[#0F1A2A]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-white group-hover:text-[#C9A64A] transition-colors">
                  Headcount & Payroll Planner
                </h3>
                <span className="px-2 py-0.5 text-[10px] font-bold bg-[#C9A64A] text-[#0F1A2A] rounded-full">
                  FULL APP
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Professional workforce planning with org charts, multi-currency support, and 12-month projections
              </p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen hero-gradient font-sans">
      {/* Header */}
      <header className="bg-[#0F1A2A]/95 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {activeTab ? (
              <button 
                onClick={() => setActiveTab(null)}
                className="flex items-center gap-2 text-gray-300 hover:text-[#C9A64A] transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm font-medium">All Calculators</span>
              </button>
            ) : (
              <Link to="/" className="flex items-center gap-2 text-gray-300 hover:text-[#C9A64A] transition-colors group">
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm font-medium">Back to Home</span>
              </Link>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-[#C9A64A] to-[#D4B76A] shadow-lg">
              <PieChart className="text-[#0F1A2A] w-5 h-5" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-bold text-base text-white">FP&A Toolkit</h1>
              <p className="text-[10px] text-[#C9A64A] uppercase tracking-wider font-semibold">Financial Suite</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {activeTab ? renderContent() : <TilesView />}
      </main>
    </div>
  );
}
